import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Table,
  Modal,
  Select,
  Input,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Upload,
  message,
  DatePicker,
  Spin,
  Typography,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import moment from 'moment';
import dayjs from '../../../utils/dayjsSetup';
import {
  getConsultantsWithWallet,
  getConsultantWalletDetails,
  getConsultantMonthlyPaymentTracking,
  listPaymentTrackingForConsultants,
  uploadExcelWithSettlement,
  getConsultantRemainingHistory,
} from '../../../services/consultantPayout';
import { trackActivityApi } from '../../../services/activityLog';
import useDownloadWithPassword from '../../../hooks/useDownloadWithPassword';
import { dateFormat } from '../../../utils/dateFormat';
import constants from '../../../utils/constants';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const PAYMENT_GATEWAY_PERCENT = 2.5;
const TDS_PERCENT = 10;
const ACCOUNTING_STORAGE_KEY = 'vm_admin_payout_accounting_v1';

/** Ant Design pickers: must be dayjs, not moment; invalid ISO → null */
const toPickerDayjs = (value) => {
  if (value == null || value === '') return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const rangePickerValue = (startDate, endDate) => {
  if (!startDate || !endDate) return undefined;
  const a = toPickerDayjs(startDate);
  const b = toPickerDayjs(endDate);
  return a && b ? [a, b] : undefined;
};

const rupee = (n) => `₹${Number(n || 0).toFixed(2)}`;
const signedRupee = (n) => {
  const amount = Number(n || 0);
  if (amount === 0) return '₹0.00';
  return `${amount > 0 ? '+' : ''}₹${amount.toFixed(2)}`;
};
const adjustmentColor = (n) => {
  const amount = Number(n || 0);
  if (amount > 0) return '#d97706';
  if (amount < 0) return '#be123c';
  return '#16a34a';
};
const roundPayoutRupee = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  const base = Math.trunc(n);
  const paise = Math.abs(n - base);
  return paise > 0.5 ? base + Math.sign(n) : base;
};
const rupeeRounded = (n) => `₹${roundPayoutRupee(n)}`;

/** Merge monthly payment-tracking rows with payout accounting stored in localStorage (per-period keys). */
function pickLocalAccountingForMonth(consultantId, monthYm, accountingByConsultant) {
  const root = accountingByConsultant?.[consultantId];
  if (!root || typeof root !== 'object') return {};

  const pick = (node) => {
    if (!node || typeof node !== 'object') return {};
    const out = {};
    if (Object.prototype.hasOwnProperty.call(node, 'paymentDate')) out.paymentDate = node.paymentDate;
    if (Object.prototype.hasOwnProperty.call(node, 'paymentStatus')) out.paymentStatus = node.paymentStatus;
    return out;
  };

  const lastYm = dayjs().subtract(1, 'month').format('YYYY-MM');
  const curYm = dayjs().format('YYYY-MM');

  if (monthYm === lastYm && root.lastmonth) return pick(root.lastmonth);
  if (monthYm === curYm && root.currentmonth) return pick(root.currentmonth);

  for (const key of Object.keys(root)) {
    const m = /^custom:([^:]+):([^:]+)$/.exec(key);
    if (!m) continue;
    const sm = dayjs(m[1]).format('YYYY-MM');
    const em = dayjs(m[2]).format('YYYY-MM');
    if (sm === monthYm && em === monthYm) return pick(root[key]);
  }

  const hasLegacy = root.paymentDate || root.paymentStatus;
  if (hasLegacy && (monthYm === lastYm || monthYm === curYm)) return pick(root);

  return {};
}

function mergePaymentTrackingForMonth(rowMonth, apiByMonth, consultantId, accountingByConsultant) {
  const api = apiByMonth?.[rowMonth] || {};
  const local = pickLocalAccountingForMonth(consultantId, rowMonth, accountingByConsultant);
  let paymentDate = api.paymentDate ?? null;
  let paymentStatus = api.paymentStatus ?? null;
  let dueAmount = api.dueAmount != null ? Number(api.dueAmount) : null;
  if (!Number.isFinite(dueAmount)) dueAmount = null;
  let dueOrHoldReason = api.dueOrHoldReason ?? null;
  if (!paymentDate && local.paymentDate != null && local.paymentDate !== '') paymentDate = local.paymentDate;
  if ((paymentStatus == null || paymentStatus === '') && local.paymentStatus)
    paymentStatus = local.paymentStatus;
  return {
    paymentDate,
    paymentStatus,
    dueAmount,
    dueOrHoldReason,
    grossAmount: api.grossAmount != null ? Number(api.grossAmount) : null,
    payableAmount: api.payableAmount != null ? Number(api.payableAmount) : null,
    transferredAmount: api.transferredAmount != null ? Number(api.transferredAmount) : null,
    holdAmount: api.holdAmount != null ? Number(api.holdAmount) : null,
    shiftToNextMonth: Boolean(api.shiftToNextMonth),
    tdsDeducted: api.tdsDeducted == null ? null : Boolean(api.tdsDeducted),
    tdsAmount: api.tdsAmount != null ? Number(api.tdsAmount) : null,
    totalTdsAvailable: api.totalTdsAvailable != null ? Number(api.totalTdsAvailable) : null,
  };
}

const normalizeTrackingRow = (row = {}) => {
  const numberOrNull = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  return {
    paymentDate: row.paymentDate ? row.paymentDate : null,
    paymentStatus: row.paymentStatus || null,
    dueAmount: numberOrNull(row.dueAmount),
    dueOrHoldReason: row.dueOrHoldReason || null,
    grossAmount: numberOrNull(row.grossAmount),
    payableAmount: numberOrNull(row.payableAmount),
    transferredAmount: numberOrNull(row.transferredAmount),
    holdAmount: numberOrNull(row.holdAmount),
    shiftToNextMonth: Boolean(row.shiftToNextMonth),
    tdsDeducted: row.tdsDeducted == null ? null : Boolean(row.tdsDeducted),
    tdsAmount: numberOrNull(row.tdsAmount),
    totalTdsAvailable: numberOrNull(row.totalTdsAvailable),
  };
};

/** Only "Last Month" allows editing payment date/status on the main table. */
/** Previous calendar month — always the month key for Last payment date/status on the main table. */
function mainTableLastMonthPaymentTrackingYm() {
  return dayjs().subtract(1, 'month').format('YYYY-MM');
}

const BREAKDOWN_BORDER_OUTER = '#3b82f6';
const BREAKDOWN_BORDER_HEADER = '#7dd3fc';
const BREAKDOWN_BORDER_CELL = '#bae6fd';

/** Main payout table: outer frame; column “boxes” with light borders */
const WALLET_HEADER_RULE = '#cbd5e1';
const WALLET_TABLE_FONT = 14;

/** S.No … Details | Chat … Wallet refund | Total … Final payable */
const WALLET_COLUMN_BOX = {
  identity: {
    border: '#c7d2fe',
    headerBg: 'linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)',
  },
  channel: {
    border: '#7dd3fc',
    headerBg: 'linear-gradient(180deg, #ecfeff 0%, #cffafe 100%)',
  },
  payout: {
    border: '#fcd34d',
    headerBg: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
  },
};

/** One line between groups: only right edge on last col of each box; left edge only on first box (S.No) */
const walletBoxEdge = (boxKey, edge) => {
  const b = WALLET_COLUMN_BOX[boxKey];
  return {
    ...(edge === 'first' && boxKey === 'identity' ? { borderLeft: `2px solid ${b.border}` } : {}),
    ...(edge === 'last' ? { borderRight: `2px solid ${b.border}` } : {}),
  };
};

const walletBoxHeaderCell = (boxKey, edge) => () => {
  const b = WALLET_COLUMN_BOX[boxKey];
  return {
    style: {
      color: '#334155',
      fontWeight: 600,
      textAlign: 'center',
      fontSize: WALLET_TABLE_FONT,
      lineHeight: 1.3,
      whiteSpace: 'normal',
      verticalAlign: 'middle',
      background: b.headerBg,
      borderBottom: `1px solid ${WALLET_HEADER_RULE}`,
      ...walletBoxEdge(boxKey, edge),
    },
  };
};

/** Body: same neutral stripe as rest of table; color only on header */
const walletBoxBodyCell = (boxKey, edge) => (_, rowIndex) => ({
  style: {
    fontSize: WALLET_TABLE_FONT,
    verticalAlign: 'middle',
    background: rowIndex % 2 === 0 ? '#ffffff' : '#fafafa',
    ...walletBoxEdge(boxKey, edge),
  },
});

const walletRestHeaderCell = () => () => ({
  style: {
    color: '#334155',
    fontWeight: 600,
    textAlign: 'center',
    fontSize: WALLET_TABLE_FONT,
    lineHeight: 1.3,
    whiteSpace: 'normal',
    verticalAlign: 'middle',
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderBottom: `1px solid ${WALLET_HEADER_RULE}`,
  },
});

const walletRestBodyCell = (_, rowIndex) => ({
  style: {
    fontSize: WALLET_TABLE_FONT,
    verticalAlign: 'middle',
    background: rowIndex % 2 === 0 ? '#ffffff' : '#fafafa',
  },
});

const breakdownHeaderCellProps = (opts = {}) => ({
  style: {
    background: 'linear-gradient(180deg, #f1f5f9 0%, #e8eef7 100%)',
    color: '#334155',
    fontWeight: 600,
    textAlign: opts.textAlign ?? 'center',
    borderBottom: `2px solid ${BREAKDOWN_BORDER_HEADER}`,
    borderRight: `1px solid ${BREAKDOWN_BORDER_HEADER}`,
    ...(opts.isFirst ? { borderLeft: `1px solid ${BREAKDOWN_BORDER_HEADER}` } : {}),
  },
});

const breakdownBodyCellProps = (opts = {}) => (_, rowIndex) => ({
  style: {
    background: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
    textAlign: opts.textAlign ?? 'center',
    borderRight: `1px solid ${BREAKDOWN_BORDER_CELL}`,
    borderBottom: `1px solid ${BREAKDOWN_BORDER_CELL}`,
    ...(opts.isFirst ? { borderLeft: `1px solid ${BREAKDOWN_BORDER_CELL}` } : {}),
  },
});

/** Same as `breakdownBodyCellProps()` — kept for older references / HMR bundles */
const breakdownZebraCell = breakdownBodyCellProps();

/** Stage 1 month table: 3 box pairs — only outer edges of each pair get thick borders (see `vm-stage1-month-table` CSS). */
const stage1Th = (opts = {}) => () => ({
  className: [
    opts.a1 && 'vm-s1-a1',
    opts.a2 && 'vm-s1-a2',
    opts.b1 && 'vm-s1-b1',
    opts.b2 && 'vm-s1-b2',
    opts.c1 && 'vm-s1-c1',
    opts.c2 && 'vm-s1-c2',
    opts.thG1 && 'vm-s1-hg1',
    opts.thG2 && 'vm-s1-hg2',
    opts.thG3 && 'vm-s1-hg3',
  ]
    .filter(Boolean)
    .join(' '),
  style: { textAlign: 'center', padding: '8px 4px', fontSize: 12 },
});

const stage1Td = (opts = {}) => () => ({
  className: [
    opts.a1 && 'vm-s1-a1',
    opts.a2 && 'vm-s1-a2',
    opts.b1 && 'vm-s1-b1',
    opts.b2 && 'vm-s1-b2',
    opts.c1 && 'vm-s1-c1',
    opts.c2 && 'vm-s1-c2',
  ]
    .filter(Boolean)
    .join(' '),
  style: { textAlign: 'center', padding: '6px 4px', fontSize: 13 },
});

/** Match `TransactionTile` labels for table + CSV */
const getTxnTypeLabel = (transaction) => {
  if (transaction?.walletDeductReason === 'vmps_share') return 'VMPS Share';
  if (transaction?.formDetails?.typeOfConsult) return transaction.formDetails.typeOfConsult;
  return transaction?.transactionFor || (transaction?.transactionType === 0 ? 'credit' : 'debit');
};

const getTxnDetailLine = (transaction) => {
  if (transaction?.transactionFor === 'consult' || transaction?.transactionFor === 'gift') {
    const duration = transaction?.meta?.callDuration;
    if (typeof duration === 'number' && !Number.isNaN(duration)) {
      const minutes = Math.floor(duration);
      const seconds = Math.round((duration - minutes) * 60);
      return `Consult with ${transaction?.meta?.name || 'User'} for ${minutes}m ${seconds}s`;
    }
  }
  if (transaction?.walletDeductReason === 'vmps_share') {
    const share = transaction?.meta?.sharePercent;
    const date = transaction?.meta?.date;
    return `VMPS share ${share ?? ''}%${date ? ` for ${date}` : ''}`;
  }
  return transaction?.walletDeductReason
    ? transaction.walletDeductReason
    : transaction?.transactionFor || 'Transaction';
};

const formatTxnDuration = (transaction) => {
  const d = transaction?.meta?.callDuration;
  if (typeof d !== 'number' || Number.isNaN(d)) return '—';
  const m = Math.floor(d);
  const s = Math.round((d - m) * 60);
  return `${m}m ${s}s`;
};

/** Stage 3: one search string matches name, userId, or orderId (substring, case-insensitive, OR). */
function filterConsultationHistoryRows(transactions, query) {
  if (!transactions?.length) return [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return transactions;
  return transactions.filter((t) => {
    const name = String(t?.meta?.name || '').toLowerCase();
    const userId = String(t?.meta?.userId || '').toLowerCase();
    const orderId = String(t?.meta?.orderId || '').toLowerCase();
    return name.includes(q) || userId.includes(q) || orderId.includes(q);
  });
}

/** Same net / PG / TDS / final ladder as `attachConsultantPayoutBreakdown` filterPeriod (per calendar month). */
const deriveMonthPayoutLadder = ({ totalRevenue, walletAdd, walletDeduct, walletRefund, payableAfterShare }) => {
  const tr = Number(totalRevenue) || 0;
  const wa = Number(walletAdd) || 0;
  const wd = Number(walletDeduct) || 0;
  const wr = Number(walletRefund) || 0;
  const netBeforeChargesRaw = payableAfterShare != null && payableAfterShare !== ''
    ? Number(payableAfterShare)
    : tr + wa - wd + wr;
  const netBeforeCharges = Number.isFinite(netBeforeChargesRaw) ? netBeforeChargesRaw : 0;
  const pg = (Math.max(0, netBeforeCharges) * PAYMENT_GATEWAY_PERCENT) / 100;
  const afterPg = Math.max(0, netBeforeCharges) - pg;
  const tds = (afterPg * TDS_PERCENT) / 100;
  const finalPay = afterPg - tds;
  return { netBeforeCharges, pg, tds, finalPay };
};

const downloadCsvFile = (filename, headers, rows, label = '') => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const line = (obj) => headers.map((h) => esc(obj[h])).join(',');
  const csv = [headers.join(','), ...rows.map((r) => line(r))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  // Track the download in activity logs
  try {
    const email = localStorage.getItem('vedic_email') || 'Admin';
    const desc = label
      ? `${email} downloaded ${label} payout sheet`
      : `${email} downloaded payout sheet: ${filename}`;
    trackActivityApi({
      action: 'EXPORT', module: 'Consultant Payout',
      description: desc, resourceId: '', resourceType: '',
    }).catch(() => { });
  } catch { /* non-fatal */ }
};

const getMonthPeriodLabel = (monthKey) => {
  const start = dayjs(`${monthKey}-01`);
  if (!start.isValid()) return 'Period';
  return `${start.format('DD/MM/YYYY')} - ${start.add(1, 'month').format('DD/MM/YYYY')}`;
};

const getPhoneNumber = (consultant) => {
  const phone = consultant?.phone || consultant?.mobile || '';
  const prefix = consultant?.phonePrefix || consultant?.countryCode || '';
  return phone ? [prefix, phone].filter(Boolean).join(' ') : '';
};

const buildConsultantPayoutCsvRows = (consultant, rows) => {
  return rows.map((r) => {
    const periodLabel = getMonthPeriodLabel(r.month);
    const totalEarning = r.grossAmount != null ? Number(r.grossAmount) : null;
    const tds = r.tdsAmount != null ? Number(r.tdsAmount) : null;
    const payable = r.payableAmount != null ? Number(r.payableAmount) : null;
    const pg = r.monthPg != null
      ? Number(r.monthPg)
      : totalEarning != null && payable != null && tds != null
        ? Number((totalEarning - payable - tds).toFixed(2))
        : null;
    return {
      Name: consultant?.accountName || consultant?.name || '',
      Email: consultant?.email || '',
      DateOfJoining: consultant?.createdAt ? dayjs(consultant.createdAt).format('DD/MM/YYYY HH:mm:ss') : '',
      'Total Earning': totalEarning != null ? totalEarning.toFixed(2) : '',
      'PG (2.5%)': pg != null ? pg.toFixed(2) : '',
      'TDS (10%)': tds != null ? tds.toFixed(2) : '',
      LastMonthFinalPayable: r.payableAmount != null ? String(roundPayoutRupee(r.payableAmount)) : '',
      Balance:
        r.holdAmount != null
          ? Number(r.holdAmount).toFixed(2)
          : r.dueAmount != null
            ? Number(r.dueAmount).toFixed(2)
            : '',
      FinalPayableAmount: r.payableAmount != null ? String(roundPayoutRupee(r.payableAmount)) : '',
      PAN: consultant?.panNumber || '',
      PhoneNumber: getPhoneNumber(consultant),
      BankName: consultant?.bankName || '',
      BankAccountNumber: consultant?.accountNumber || '',
      BankIFSCCode: consultant?.ifsc || '',
      BankHolderName: consultant?.bankHolderName || '',
      PayoutMonth: r.monthLabel || r.month || '',
      TransactionId: '',
      PayoutDate: r.paymentDate ? dayjs(r.paymentDate).format('DD/MM/YYYY HH:mm:ss') : '',
      Status: r.paymentStatus || '',
      Period: periodLabel === 'Period' ? '' : periodLabel,
    };
  });
};

const loadAccountingMap = () => {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTING_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const ConsultantWallet = () => {
  const [loading, setLoading] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [consultantDetailModalVisible, setConsultantDetailModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [accountingByConsultant, setAccountingByConsultant] = useState(loadAccountingMap);
  const [breakdownRange, setBreakdownRange] = useState('selected'); // selected | currentmonth | lastmonth | last6months
  const [paymentTrackingByMonth, setPaymentTrackingByMonth] = useState({}); // DB-backed per consultant month
  const [paymentTrackingRows, setPaymentTrackingRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  /** Modal: day-wise drill-down — ordered YYYY-MM, tables stacked */
  const [modalExpandedMonthKeys, setModalExpandedMonthKeys] = useState([]);
  /** Consultant detail modal: which stage panel is visible (1 | 2 | 3) */
  const [detailModalStage, setDetailModalStage] = useState('1');
  /** Stage 3 consultation history: single search across name / user ID / order ID */
  const [stage3TxnSearch, setStage3TxnSearch] = useState('');
  const [paymentDetailModalOpen, setPaymentDetailModalOpen] = useState(false);
  const [paymentDetailConsultantId, setPaymentDetailConsultantId] = useState(null);
  const [remainingHistory, setRemainingHistory] = useState([]);
  const [remainingHistoryLoading, setRemainingHistoryLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    filter: 'today',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const filterOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Current Month', value: 'currentmonth' },
    { label: 'Last Month', value: 'lastmonth' },
    { label: 'Last 3 Months', value: 'last3months' },
    { label: 'Last 6 Months', value: 'last6months' },
    { label: 'Last Year', value: 'lastyear' },
    { label: 'Custom', value: 'custom' },
  ];

  const selectedFilterLabel = filterOptions.find((o) => o.value === filters.filter)?.label || 'Period';
  const totalRevenueColumnTitle =
    filters.filter === 'custom' && filters.startDate && filters.endDate
      ? `Total revenue (custom)`
      : `Total Earning (${selectedFilterLabel})`;

  useEffect(() => {
    localStorage.setItem(ACCOUNTING_STORAGE_KEY, JSON.stringify(accountingByConsultant));
  }, [accountingByConsultant]);

  useEffect(() => {
    if (!consultantDetailModalVisible) {
      setStage3TxnSearch('');
    }
  }, [consultantDetailModalVisible]);

  useEffect(() => {
    if (!consultantDetailModalVisible) return;
    if (!selectedConsultant?._id) return;

    const consultantId = selectedConsultant._id;
    setPaymentTrackingByMonth({});
    setPaymentTrackingRows([]);

    (async () => {
      try {
        const response = await getConsultantMonthlyPaymentTracking({ consultantId });
        const data = response.data;
        if (!data.success) return;

        const rows = data.result || [];
        const map = {};
        for (const row of rows) {
          map[row.month] = normalizeTrackingRow(row);
        }
        setPaymentTrackingRows(rows);
        setPaymentTrackingByMonth(map);
      } catch (e) {
        console.error('Failed to fetch monthly payment tracking', e);
      }
    })();
  }, [consultantDetailModalVisible, selectedConsultant?._id]);

  /** Stage 2: when no month is selected yet, default to the current calendar month (if present), else the newest month in the list. */
  const stage2AvailableMonthKeys = (selectedConsultant?.payoutBreakdown?.last6Months || [])
    .map((r) => r?.month)
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    if (!consultantDetailModalVisible) return;
    if (detailModalStage !== '2') return;
    if (!selectedConsultant?._id) return;

    const months = stage2AvailableMonthKeys ? stage2AvailableMonthKeys.split(',').filter(Boolean) : [];
    if (!months.length) return;

    setModalExpandedMonthKeys((prev) => {
      if (prev.length > 0) return prev;
      const currentYm = moment().format('YYYY-MM');
      const pick = months.includes(currentYm) ? currentYm : months[0];
      return [pick];
    });
  }, [
    consultantDetailModalVisible,
    detailModalStage,
    selectedConsultant?._id,
    stage2AvailableMonthKeys,
  ]);

  useEffect(() => {
    if (!consultantDetailModalVisible) {
      setModalExpandedMonthKeys([]);
      setDetailModalStage('1');
    }
  }, [consultantDetailModalVisible]);

  useEffect(() => {
    setModalExpandedMonthKeys([]);
    setDetailModalStage('1');
  }, [selectedConsultant?._id]);

  const getAccountingPeriodKey = useCallback(() => {
    if (filters.filter === 'custom' && filters.startDate && filters.endDate) {
      return `custom:${filters.startDate}:${filters.endDate}`;
    }
    return filters.filter || 'today';
  }, [filters.filter, filters.startDate, filters.endDate]);

  const getAccountingForConsultant = useCallback(
    (consultantId) => {
      const periodKey = getAccountingPeriodKey();
      const legacyAcc = accountingByConsultant[consultantId] || {};
      const perPeriodAcc = legacyAcc?.[periodKey] || {};

      // Backward compatibility: old shape stored values at top-level.
      const hasLegacy = legacyAcc?.paymentDate || legacyAcc?.paymentStatus;
      if (Object.keys(perPeriodAcc || {}).length === 0 && hasLegacy) {
        return { paymentDate: legacyAcc.paymentDate, paymentStatus: legacyAcc.paymentStatus };
      }

      return perPeriodAcc || {};
    },
    [accountingByConsultant, getAccountingPeriodKey]
  );


  // Migrate old (unscoped) accounting values into the currently selected period key.
  useEffect(() => {
    const periodKey = getAccountingPeriodKey();
    let changed = false;

    setAccountingByConsultant((prev) => {
      const next = { ...prev };
      for (const consultantId of Object.keys(prev || {})) {
        const legacyAcc = prev[consultantId];
        if (!legacyAcc) continue;

        const hasLegacy = legacyAcc.paymentDate || legacyAcc.paymentStatus;
        const perPeriodAcc = legacyAcc?.[periodKey];

        if (hasLegacy && (!perPeriodAcc || Object.keys(perPeriodAcc || {}).length === 0)) {
          next[consultantId] = {
            ...legacyAcc,
            [periodKey]: { paymentDate: legacyAcc.paymentDate, paymentStatus: legacyAcc.paymentStatus },
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccountingPeriodKey]);

  useEffect(() => {
    // Reset modal table view when opening for a consultant.
    if (consultantDetailModalVisible) {
      if (filters.filter === 'lastmonth') setBreakdownRange('lastmonth');
      else if (filters.filter === 'last6months') setBreakdownRange('last6months');
      else if (filters.filter === 'currentmonth') setBreakdownRange('currentmonth');
      else setBreakdownRange('selected');
    }
    if (consultantDetailModalVisible) setDetailModalStage('1');
  }, [consultantDetailModalVisible]);

  const setAccountingField = useCallback(
    (consultantId, patch) => {
      const periodKey = getAccountingPeriodKey();
      setAccountingByConsultant((prev) => ({
        ...prev,
        [consultantId]: {
          ...(prev[consultantId] || {}),
          [periodKey]: {
            ...((prev[consultantId] || {})[periodKey] || {}),
            ...patch,
          },
        },
      }));
    },
    [getAccountingPeriodKey]
  );

  /** Main-table payment UI: row state + DB only (do not use localStorage for these fields). */
  const patchConsultantRowPayment = useCallback((consultantId, patch) => {
    setConsultants((prev) =>
      prev.map((c) => (String(c._id) === String(consultantId) ? { ...c, ...patch } : c))
    );
  }, []);

  const mergeListRowPaymentDisplay = useCallback(
    (consultantId, record) => {
      const periodKey = getAccountingPeriodKey();
      const root = accountingByConsultant[consultantId] || {};
      const bucket = root[periodKey];
      const bucketDateOverride =
        bucket &&
        Object.prototype.hasOwnProperty.call(bucket, 'paymentDate') &&
        bucket.paymentDate != null &&
        bucket.paymentDate !== '';
      const bucketStatusOverride =
        bucket &&
        Object.prototype.hasOwnProperty.call(bucket, 'paymentStatus') &&
        bucket.paymentStatus != null &&
        bucket.paymentStatus !== '';
      const localMerged = getAccountingForConsultant(consultantId);

      // Prefer DB / list row first (source of truth); legacy localStorage only as fallback.
      let paymentDate = null;
      if (record?.periodPaymentDate) {
        const pd = record.periodPaymentDate;
        paymentDate = typeof pd === 'string' ? pd : new Date(pd).toISOString();
      }
      let paymentStatus = record?.periodPaymentStatus ?? null;

      if (bucketDateOverride) paymentDate = bucket.paymentDate;
      if (bucketStatusOverride) paymentStatus = bucket.paymentStatus;

      if (!paymentDate && localMerged.paymentDate != null && localMerged.paymentDate !== '')
        paymentDate = localMerged.paymentDate;
      if ((paymentStatus == null || paymentStatus === '') && localMerged.paymentStatus)
        paymentStatus = localMerged.paymentStatus;

      let dueAmount =
        record?.periodDueAmount != null && record?.periodDueAmount !== ''
          ? Number(record.periodDueAmount)
          : null;
      if (!Number.isFinite(dueAmount)) dueAmount = null;
      const dueOrHoldReason = record?.periodDueOrHoldReason ?? null;

      return {
        paymentDate,
        paymentStatus,
        dueAmount,
        dueOrHoldReason,
        grossAmount: record?.periodGrossAmount != null ? Number(record.periodGrossAmount) : null,
        payableAmount: record?.periodPayableAmount != null ? Number(record.periodPayableAmount) : null,
        transferredAmount: record?.periodTransferredAmount != null ? Number(record.periodTransferredAmount) : null,
        holdAmount: record?.periodHoldAmount != null ? Number(record.periodHoldAmount) : null,
        shiftToNextMonth: Boolean(record?.periodShiftToNextMonth),
        tdsDeducted: record?.periodTdsDeducted == null ? null : Boolean(record.periodTdsDeducted),
        tdsAmount: record?.periodTdsAmount != null ? Number(record.periodTdsAmount) : null,
        totalTdsAvailable: record?.periodTotalTdsAvailable != null ? Number(record.periodTotalTdsAvailable) : null,
      };
    },
    [accountingByConsultant, getAccountingForConsultant, getAccountingPeriodKey]
  );

  const fetchConsultants = async () => {
    setLoading(true);
    try {
      const response = await getConsultantsWithWallet({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      });
      const data = response.data;
      if (data.success) {
        let rows = data.result.consultants || [];
        const ym = mainTableLastMonthPaymentTrackingYm();
        if (rows.length > 0) {
          try {
            const tr = await listPaymentTrackingForConsultants({
              month: ym,
              consultantIds: rows.map((r) => r._id),
            });
            if (tr?.data?.success && Array.isArray(tr.data.result)) {
              const byId = {};
              for (const r of tr.data.result) {
                byId[String(r.consultantId)] = r;
              }
              rows = rows.map((c) => {
                const t = byId[String(c._id)];
                if (!t) return { ...c };
                return {
                  ...c,
                  periodPaymentDate: t.paymentDate ?? c.periodPaymentDate ?? null,
                  periodPaymentStatus: t.paymentStatus ?? c.periodPaymentStatus ?? null,
                  periodDueAmount: t.dueAmount != null ? Number(t.dueAmount) : c.periodDueAmount ?? null,
                  periodDueOrHoldReason: t.dueOrHoldReason ?? c.periodDueOrHoldReason ?? null,
                  periodGrossAmount: t.grossAmount != null ? Number(t.grossAmount) : c.periodGrossAmount ?? null,
                  periodPayableAmount: t.payableAmount != null ? Number(t.payableAmount) : c.periodPayableAmount ?? null,
                  periodTransferredAmount:
                    t.transferredAmount != null ? Number(t.transferredAmount) : c.periodTransferredAmount ?? null,
                  periodHoldAmount: t.holdAmount != null ? Number(t.holdAmount) : c.periodHoldAmount ?? null,
                  periodShiftToNextMonth: t.shiftToNextMonth ?? c.periodShiftToNextMonth ?? false,
                  periodTdsDeducted: t.tdsDeducted ?? c.periodTdsDeducted ?? null,
                  periodTdsAmount: t.tdsAmount != null ? Number(t.tdsAmount) : c.periodTdsAmount ?? null,
                  periodTotalTdsAvailable:
                    t.totalTdsAvailable != null ? Number(t.totalTdsAvailable) : c.periodTotalTdsAvailable ?? null,
                };
              });
            }
          } catch (enrichErr) {
            console.error('Payment tracking batch fetch failed', enrichErr);
          }
        }
        setConsultants(rows);
        setPagination((prev) => ({
          ...prev,
          total: data.result.total || 0,
        }));
      }
    } catch (error) {
      toast.error('Failed to fetch consultants');
      console.error('Error fetching consultants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultantWalletDetails = async (consultantId) => {
    try {
      setDetailLoading(true);
      const payload = { filter: filters.filter };
      if (filters.filter === 'custom' && filters.startDate && filters.endDate) {
        payload.startDate = filters.startDate;
        payload.endDate = filters.endDate;
      }

      const response = await getConsultantWalletDetails(consultantId, payload);
      const data = response.data;
      if (data.success) {
        setSelectedConsultant(data.result);
        setConsultantDetailModalVisible(true);
      }
    } catch (error) {
      toast.error('Failed to fetch consultant wallet details');
      console.error('Error fetching consultant wallet details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const refetchConsultantWalletDetails = async (consultantId, payload) => {
    try {
      setDetailLoading(true);
      const response = await getConsultantWalletDetails(consultantId, payload);
      const data = response.data;
      if (data.success) {
        setSelectedConsultant(data.result);
      }
    } catch (error) {
      toast.error('Failed to fetch consultant wallet details');
      console.error('Error fetching consultant wallet details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultants();
  }, [pagination.current, pagination.pageSize, filters]);

  const handleTableChange = (paginationInfo, _filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));

    if (sorter.field) {
      setFilters((prev) => ({
        ...prev,
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
      }));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  /** Same period options as main page; updates list filters and refetches open consultant detail */
  const patchDetailFiltersAndRefetch = useCallback(
    async (patch) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      setPagination((prev) => ({ ...prev, current: 1 }));
      if (selectedConsultant?._id) {
        const payload = { filter: next.filter };
        if (next.filter === 'custom' && next.startDate && next.endDate) {
          payload.startDate = next.startDate;
          payload.endDate = next.endDate;
        }
        await refetchConsultantWalletDetails(selectedConsultant._id, payload);
      }
    },
    [filters, selectedConsultant?._id]
  );

  const { loading: exportbuttonLoader, downloadFile, requestDownload, requestDownloadWithCallback, DownloadPasswordModal } = useDownloadWithPassword();

  const handleDownloadExcel = async () => {
    try {
      const params = {
        filter: filters.filter,
        search: filters.search || undefined,
        ...(filters.filter === 'custom' && filters.startDate && filters.endDate
          ? {
            startDate: filters.startDate,
            endDate: filters.endDate,
          }
          : {}),
      };

      const URL = constants.SERVER_API;
      const url = `${URL}/admin/payout/export-with-period`;

      requestDownload(url, `user-excel-file-${dateFormat(new Date())}`, params);
    } catch (error) {
      toast.error('Failed to download Excel file');
      console.error('Error downloading Excel:', error);
    }
  };

  const handleDownloadBankPanPendingExcel = async () => {
    try {
      const params = {
        filter: filters.filter,
        bankPanApproval: 'missing',
        search: filters.search || undefined,
        ...(filters.filter === 'custom' && filters.startDate && filters.endDate
          ? {
            startDate: filters.startDate,
            endDate: filters.endDate,
          }
          : {}),
      };

      const URL = constants.SERVER_API;
      const url = `${URL}/admin/payout/export-with-period`;

      requestDownload(url, `bank-pan-not-approved-${dateFormat(new Date())}`, params);
    } catch (error) {
      toast.error('Failed to download Bank/PAN not approved Excel file');
      console.error('Error downloading Bank/PAN not approved Excel:', error);
    }
  };

  const handleUserDownloadExcel = async () => {
    try {
      const params = {
        filter: filters.filter,
        ...(filters.filter === 'custom' && filters.startDate && filters.endDate
          ? {
            startDate: filters.startDate,
            endDate: filters.endDate,
          }
          : {}),
      };

      const URL = constants.SERVER_API;
      const url = `${URL}/admin/user/get-user-payments-sheet-all`;

      requestDownload(
        url,
        `user-payment-data-${dateFormat(new Date())}.xlsx`,
        params,
        'Consultant Payout — User Payment Data'
      );
    } catch (error) {
      toast.error('Failed to download User Excel file');
      console.error('Error downloading User Excel:', error);
    }
  };

  const handleDownloadSettlementTemplate = async () => {
    try {
      const url = `${constants.SERVER_API}/admin/payout/settlement-template`;
      const downloaded = await downloadFile(url, `payout-settlement-template-${dateFormat(new Date())}.xlsx`, {});
      if (!downloaded) toast.error('Failed to download payout template');
    } catch (error) {
      toast.error('Failed to download payout template');
      console.error('Error downloading payout template:', error);
    }
  };

  const handleConsultantDownloadExcel = async () => {
    try {
      const params = {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
      };

      const URL = constants.SERVER_API;
      const url = `${URL}/admin/consultant/wallet/payment-tracking-sheet-all`;

      requestDownload(
        url,
        `consultant-payment-tracking-${dateFormat(new Date())}.xlsx`,
        params,
        'Consultant Payout — Consultant Payment Tracking'
      );
    } catch (error) {
      toast.error('Failed to download Consultant Excel file');
      console.error('Error downloading Consultant Excel:', error);
    }
  };

  const handleUploadExcel = async (file) => {
    try {
      setUploadBusy(true);
      const formData = new FormData();
      formData.append('payOut', file);

      const response = await uploadExcelWithSettlement(formData);

      if (response.data.success) {
        toast.success(response.data.message);
        fetchConsultants();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to upload Excel file');
      console.error('Error uploading Excel:', error);
    } finally {
      setUploadBusy(false);
    }
  };

  const popupContainer = () => document.body;

  const paymentDetailsColumnTitle = 'Payout details';

  useEffect(() => {
    if (!paymentDetailModalOpen || !paymentDetailConsultantId) {
      setRemainingHistory([]);
      return;
    }
    let cancelled = false;
    setRemainingHistoryLoading(true);
    getConsultantRemainingHistory(paymentDetailConsultantId)
      .then((response) => {
        if (cancelled) return;
        setRemainingHistory(response.data?.success ? response.data.result || [] : []);
      })
      .catch(() => {
        if (!cancelled) setRemainingHistory([]);
      })
      .finally(() => {
        if (!cancelled) setRemainingHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paymentDetailModalOpen, paymentDetailConsultantId]);

  const closePaymentDetailModal = () => {
    setPaymentDetailModalOpen(false);
    setPaymentDetailConsultantId(null);
  };

  const columns = [
    {
      title: 'S.No',
      key: 'ranking',
      width: 52,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('identity', 'first'),
      onCell: walletBoxBodyCell('identity', 'first'),
      render: (_, _record, index) => (
        <div style={{ textAlign: 'center' }}>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
    },
    {
      title: 'Consultant Name',
      key: 'consultant',
      width: 300,
      onHeaderCell: walletBoxHeaderCell('identity', 'middle'),
      onCell: walletBoxBodyCell('identity', 'middle'),
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={record.profileImage || '/default-avatar.png'}
            alt={record.accountName}
            style={{
              width: 40,
              height: 40,
              marginRight: 12,
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: WALLET_TABLE_FONT + 1 }}>{record.accountName}</div>
            <div
              style={{
                fontSize: WALLET_TABLE_FONT,
                color: '#666',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                lineHeight: 1.35,
              }}
            >
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Consultant ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 96,
      ellipsis: true,
      onHeaderCell: walletBoxHeaderCell('identity', 'middle'),
      onCell: walletBoxBodyCell('identity', 'middle'),
    },
    {
      title: 'Lifetime earning',
      key: 'lifeTimeEarning',
      width: 118,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('identity', 'middle'),
      onCell: walletBoxBodyCell('identity', 'middle'),
      render: (_, record) => (
        <span style={{ color: '#6366f1', fontWeight: 600 }}>{rupee(record.lifeTimeEarning)}</span>
      ),
    },
    {
      title: 'Current month earning',
      key: 'currentMonthEarning',
      width: 132,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('identity', 'middle'),
      onCell: walletBoxBodyCell('identity', 'middle'),
      render: (_, record) => {
        const v = record.currentMonthEarning ?? record.lastMonthEarning;
        return <span style={{ color: '#0ea5e9', fontWeight: 600 }}>{rupee(v)}</span>;
      },
    },
    {
      title: 'Details',
      key: 'details',
      width: 100,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('identity', 'last'),
      onCell: walletBoxBodyCell('identity', 'last'),
      render: (_, record) => (
        <Button type="default" size="small" icon={<EyeOutlined />} onClick={() => fetchConsultantWalletDetails(record._id)}>
          Details
        </Button>
      ),
    },
    {
      title: 'Chat earning',
      dataIndex: 'periodChatEarning',
      key: 'periodChatEarning',
      width: 104,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('channel', 'first'),
      onCell: walletBoxBodyCell('channel', 'first'),
      render: (v) => <span style={{ color: '#13c2c2', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: 'Call earning',
      dataIndex: 'periodCallEarning',
      key: 'periodCallEarning',
      width: 104,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('channel', 'middle'),
      onCell: walletBoxBodyCell('channel', 'middle'),
      render: (v) => <span style={{ color: '#eb2f96', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: 'Fixed session earning',
      dataIndex: 'periodFixedSessionEarning',
      key: 'periodFixedSessionEarning',
      width: 104,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('channel', 'middle'),
      onCell: walletBoxBodyCell('channel', 'middle'),
      render: (v) => <span style={{ color: '#2f54eb', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: 'Wallet Add',
      dataIndex: 'periodWalletAdd',
      key: 'periodWalletAdd',
      width: 96,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('channel', 'middle'),
      onCell: walletBoxBodyCell('channel', 'middle'),
      render: (v) => <span style={{ color: '#389e0d', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: 'Wallet Deduct',
      dataIndex: 'periodNonPayoutDebits',
      key: 'periodNonPayoutDebits',
      width: 112,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('channel', 'middle'),
      onCell: walletBoxBodyCell('channel', 'middle'),
      render: (v) => <span style={{ color: '#cf1322', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: totalRevenueColumnTitle,
      key: 'totalRevenuePeriodStack',
      width: 132,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'first'),
      onCell: walletBoxBodyCell('payout', 'first'),
      render: (_, record) => (
        <div style={{ fontSize: WALLET_TABLE_FONT, lineHeight: 1.35 }}>
          <div style={{ color: '#722ed1', fontWeight: 600 }}>{rupee(record.availableBalanceForPeriod)}</div>
        </div>
      ),
    },
    {
      title: `PG (${PAYMENT_GATEWAY_PERCENT}%)`,
      dataIndex: 'paymentGatewayCharge',
      key: 'paymentGatewayCharge',
      width: 88,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'middle'),
      onCell: walletBoxBodyCell('payout', 'middle'),
      render: (v) => <span style={{ color: '#fa8c16', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: `TDS (${TDS_PERCENT}%)`,
      dataIndex: 'tdsCharge',
      key: 'tdsCharge',
      width: 84,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'middle'),
      onCell: walletBoxBodyCell('payout', 'middle'),
      render: (v) => <span style={{ color: '#f5222d', fontWeight: 600 }}>{rupee(v)}</span>,
    },
    {
      title: 'Last Month Final Payable',
      dataIndex: 'currentPeriodPayableAmount',
      key: 'currentPeriodPayableAmount',
      width: 136,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'middle'),
      onCell: walletBoxBodyCell('payout', 'middle'),
      render: (v, record) => {
        const amount = v != null ? v : record.payableBalance;
        return <span style={{ color: '#16a34a', fontWeight: 600 }}>{rupeeRounded(amount)}</span>;
      },
    },
    {
      title: (
        <Tooltip title="Previous month leftover is added (+). If last month was overpaid, that extra is deducted (−) from this payout.">
          Prev +/− (add / deduct)
        </Tooltip>
      ),
      dataIndex: 'carryForwardBalance',
      key: 'carryForwardBalance',
      width: 150,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'middle'),
      onCell: walletBoxBodyCell('payout', 'middle'),
      render: (v, record) => {
        const amount = Number(v || 0);
        const title = amount > 0
          ? `Underpaid ${record.carryForwardFromPeriod || 'previous month'} — added into this payout`
          : amount < 0
            ? `Overpaid ${record.carryForwardFromPeriod || 'previous month'} — deducted from this payout`
            : 'No previous plus/minus';
        return (
          <Tooltip title={title}>
            <div>
              <div style={{ color: adjustmentColor(amount), fontWeight: 600 }}>{signedRupee(amount)}</div>
              {amount !== 0 && record.carryForwardFromPeriod ? (
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                  {amount > 0 ? 'add' : 'deduct'} {record.carryForwardFromPeriod}
                </div>
              ) : null}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="After this month settlement: leftover (+) goes into next month, overpaid (−) is deducted from next month.">
          This month +/−
        </Tooltip>
      ),
      dataIndex: 'thisMonthPendingBalance',
      key: 'thisMonthPendingBalance',
      width: 140,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'middle'),
      onCell: walletBoxBodyCell('payout', 'middle'),
      render: (v, record) => {
        if (v == null) {
          return <span style={{ color: '#94a3b8' }}>—</span>;
        }
        const amount = Number(v || 0);
        const title = amount > 0
          ? `${record.thisMonthPeriod || 'This month'} leftover ₹${Number(v).toFixed(2)} will add in ${record.thisMonthPendingGoesTo || 'next month'}`
          : amount < 0
            ? `${record.thisMonthPeriod || 'This month'} overpaid ₹${Math.abs(amount).toFixed(2)} will deduct in ${record.thisMonthPendingGoesTo || 'next month'}`
            : `${record.thisMonthPeriod || 'This month'} is exact paid`;
        return (
          <Tooltip title={title}>
            <div>
              <div style={{ color: adjustmentColor(amount), fontWeight: 600 }}>{signedRupee(amount)}</div>
              {record.thisMonthPeriod ? (
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{record.thisMonthPeriod}</div>
              ) : null}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="What they will get after share + PG + TDS. Wallet is not deducted. Month total is the sum of each day's final.">
          Final payable
        </Tooltip>
      ),
      dataIndex: 'payableBalance',
      key: 'payableBalance',
      width: 100,
      align: 'center',
      onHeaderCell: walletBoxHeaderCell('payout', 'last'),
      onCell: walletBoxBodyCell('payout', 'last'),
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600 }}>{rupeeRounded(v)}</span>,
    },
    {
      title: 'Profile',
      key: 'profile',
      width: 76,
      align: 'center',
      onHeaderCell: walletRestHeaderCell(),
      onCell: walletRestBodyCell,
      render: (_, record) => (
        <Link to={`/consultant-management/consultant-detail/${record._id}?from=management`} target="_blank" rel="noopener noreferrer">
          Open
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 84,
      align: 'center',
      onHeaderCell: walletRestHeaderCell(),
      onCell: walletRestBodyCell,
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>{status ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: paymentDetailsColumnTitle,
      key: 'accPaymentDetails',
      width: 240,
      onHeaderCell: walletRestHeaderCell(),
      onCell: walletRestBodyCell,
      render: (_, record) => {
        const id = record._id;
        const display = mergeListRowPaymentDisplay(id, record);
        const dateLabel = display.paymentDate ? dayjs(display.paymentDate).format('DD MMM YYYY') : '—';
        const statusLabel = display.paymentStatus || '—';
        const transferredLabel = display.transferredAmount != null ? rupee(display.transferredAmount) : '—';
        const holdLabel = display.holdAmount != null ? rupee(display.holdAmount) : display.dueAmount != null ? rupee(display.dueAmount) : '—';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
            <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.35 }}>
              <span style={{ color: '#94a3b8' }}>Date:</span> {dateLabel}
              <span style={{ margin: '0 6px', color: '#e2e8f0' }}>|</span>
              <span style={{ color: '#94a3b8' }}>Status:</span> {statusLabel}
              <br />
              <span style={{ color: '#94a3b8' }}>Transfer:</span> {transferredLabel}
              <span style={{ margin: '0 6px', color: '#e2e8f0' }}>|</span>
              <span style={{ color: '#94a3b8' }}>Hold:</span> {holdLabel}
            </div>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontWeight: 600 }}
              onClick={() => {
                setPaymentDetailConsultantId(id);
                setPaymentDetailModalOpen(true);
              }}
            >
              View payout
            </Button>
          </div>
        );
      },
    },
  ];

  const downloadTransactionHistory = () => {
    const source = filterConsultationHistoryRows(selectedConsultant?.transactions || [], stage3TxnSearch);
    if (!source.length) {
      toast.error('No transactions to download');
      return;
    }

    const rows = source.map((transaction) => {
      const amount = Number(transaction?.coins || 0);
      return {
        DateTime: moment(transaction?.createdAt).format('DD/MM/YYYY HH:mm'),
        Type: getTxnTypeLabel(transaction),
        Name: transaction?.meta?.name || '',
        Duration: formatTxnDuration(transaction) === '—' ? '' : formatTxnDuration(transaction),
        Detail: getTxnDetailLine(transaction),
        UserId: transaction?.meta?.userId || '',
        OrderId: transaction?.meta?.orderId || '',
        CreditDebit: transaction?.transactionType === 0 ? 'CREDIT' : 'DEBIT',
        Mode: transaction?.meta?.mode || '',
        Amount: amount.toFixed(2),
        TransactionFor: transaction?.transactionFor || '',
        TransactionId: transaction?.transactionId || '',
        Consultant: selectedConsultant?.accountName || '',
      };
    });

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            const value = row[header] ?? '';
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `consultant_transactions_${selectedConsultant?.accountName || 'history'}_${filters.filter}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Transaction history downloaded');

    // Track the download
    try {
      const email = localStorage.getItem('vedic_email') || 'Admin';
      trackActivityApi({
        action: 'EXPORT', module: 'Consultant Payout',
        description: `${email} downloaded Stage 3 (Consultation history) for ${selectedConsultant?.accountName || 'consultant'}`,
        resourceId: '', resourceType: '',
      }).catch(() => { });
    } catch { /* non-fatal */ }
  };

  /** Stage 2 day-wise: same column sequence + pair-box styling as Stage 1 (Date replaces Month). */
  const breakdownStage2DayCols = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 98,
      align: 'center',
      onHeaderCell: stage1Th({ a1: true, thG1: true }),
      onCell: stage1Td({ a1: true }),
    },
    {
      title: 'Orders',
      dataIndex: 'ordersCount',
      key: 'ordersCount',
      width: 64,
      align: 'center',
      onHeaderCell: stage1Th({ a2: true, thG1: true }),
      onCell: stage1Td({ a2: true }),
      render: (v) => Number(v ?? 0),
    },
    {
      title: (
        <Tooltip title="Consultant default split for that day (usually 50%). Chat/Call/Fixed credits are already this share.">
          Actual Share %
        </Tooltip>
      ),
      dataIndex: 'actualShare',
      key: 'actualShare',
      width: 96,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => (v == null || v === '' || Number(v) <= 0 ? '—' : `${Number(v)}%`),
    },
    {
      title: (
        <Tooltip title="Percent of Actual Share for selected consultants below VMPS cutoff. Example: Actual 35% and Revenue 20% → keep 20% of that day's earning (effective 7%). Empty means keep Actual Share. Wallet is not deducted.">
          Revenue Share %
        </Tooltip>
      ),
      dataIndex: 'revenueShare',
      key: 'revenueShare',
      width: 110,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => (v == null || v === '' || Number(v) <= 0 ? '—' : `${Number(v)}%`),
    },
    {
      title: (
        <Tooltip title="Actual Share × Revenue Share %. Example: 35% × 20% = 7% of total consultation revenue. Empty when VMPS share was not applied.">
          Effective Share %
        </Tooltip>
      ),
      dataIndex: 'effectiveShare',
      key: 'effectiveShare',
      width: 110,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => (v == null || v === '' || Number(v) <= 0 ? '—' : `${Number(v)}%`),
    },
    {
      title: 'Chat earning',
      dataIndex: 'chat',
      key: 'chat',
      width: 78,
      align: 'center',
      onHeaderCell: stage1Th({ b1: true, thG2: true }),
      onCell: stage1Td({ b1: true }),
      render: (v) => rupee(v),
    },
    {
      title: 'Call earning',
      dataIndex: 'call',
      key: 'call',
      width: 78,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Fixed earning',
      dataIndex: 'fixedSession',
      key: 'fixedSession',
      width: 76,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Wallet deduct',
      dataIndex: 'grossDebits',
      key: 'grossDebits',
      width: 100,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Wallet addition',
      dataIndex: 'walletAdd',
      key: 'walletAdd',
      width: 98,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: (
        <Tooltip title="Actual Chat + Call + Fixed earned. This is how much was made. Wallet stays on this amount.">
          Total Earning
        </Tooltip>
      ),
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      width: 102,
      align: 'center',
      onHeaderCell: stage1Th({ c1: true, thG3: true }),
      onCell: stage1Td({ c1: true }),
      render: (v) => rupee(v),
    },
    {
      title: `Payment gateway (${PAYMENT_GATEWAY_PERCENT}%)`,
      dataIndex: 'monthPg',
      key: 'monthPg',
      width: 108,
      align: 'center',
      onHeaderCell: stage1Th({ thG3: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: `TDS (${TDS_PERCENT}%)`,
      dataIndex: 'monthTds',
      key: 'monthTds',
      width: 80,
      align: 'center',
      onHeaderCell: stage1Th({ thG3: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: (
        <Tooltip title="What they will get after share + PG + TDS. Wallet is not deducted. Month total is the sum of each day's final.">
          Final payable
        </Tooltip>
      ),
      dataIndex: 'monthFinal',
      key: 'monthFinal',
      width: 102,
      align: 'center',
      onHeaderCell: stage1Th({ c2: true, thG3: true }),
      onCell: stage1Td({ c2: true }),
      render: (v) => rupee(v),
    },
  ];

  const breakdownStage1Cols = [
    {
      title: 'Month',
      dataIndex: 'monthLabel',
      key: 'monthLabel',
      width: 86,
      align: 'center',
      onHeaderCell: stage1Th({ a1: true, thG1: true }),
      onCell: stage1Td({ a1: true }),
    },
    {
      title: 'Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      width: 64,
      align: 'center',
      onHeaderCell: stage1Th({ a2: true, thG1: true }),
      onCell: stage1Td({ a2: true }),
      render: (v) => Number(v ?? 0),
    },
    {
      title: 'Chat earning',
      dataIndex: 'chat',
      key: 'chat',
      width: 78,
      align: 'center',
      onHeaderCell: stage1Th({ b1: true, thG2: true }),
      onCell: stage1Td({ b1: true }),
      render: (v) => rupee(v),
    },
    {
      title: 'Call earning',
      dataIndex: 'call',
      key: 'call',
      width: 78,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Fixed earning',
      dataIndex: 'fixedSession',
      key: 'fixedSession',
      width: 76,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Gift earning',
      dataIndex: 'gift',
      key: 'gift',
      width: 86,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Wallet deduct',
      dataIndex: 'grossDebits',
      key: 'grossDebits',
      width: 100,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: 'Wallet addition',
      dataIndex: 'walletAdd',
      key: 'walletAdd',
      width: 98,
      align: 'center',
      onHeaderCell: stage1Th({ thG2: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: (
        <Tooltip title="Chat + Call + Fixed + Gift + wallet addition − wallet deduct. Same figure as the list Total Earning. Payment gateway and TDS are calculated on this amount.">
          Total Earning
        </Tooltip>
      ),
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      width: 102,
      align: 'center',
      onHeaderCell: stage1Th({ c1: true, thG3: true }),
      onCell: stage1Td({ c1: true }),
      render: (v) => rupee(v),
    },
    {
      title: `Payment gateway (${PAYMENT_GATEWAY_PERCENT}%)`,
      dataIndex: 'monthPg',
      key: 'monthPg',
      width: 108,
      align: 'center',
      onHeaderCell: stage1Th({ thG3: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: `TDS (${TDS_PERCENT}%)`,
      dataIndex: 'monthTds',
      key: 'monthTds',
      width: 80,
      align: 'center',
      onHeaderCell: stage1Th({ thG3: true }),
      onCell: stage1Td(),
      render: (v) => rupee(v),
    },
    {
      title: (
        <Tooltip title="What they will get after share + PG + TDS. Wallet is not deducted. Month total is the sum of each day's final.">
          Final payable
        </Tooltip>
      ),
      dataIndex: 'monthFinal',
      key: 'monthFinal',
      width: 102,
      align: 'center',
      onHeaderCell: stage1Th({ c2: true, thG3: true }),
      onCell: stage1Td({ c2: true }),
      render: (v) => rupee(v),
    },
  ];

  /** Plain function (not a nested component) so React does not remount `<Modal />` on every parent re-render. */
  const renderConsultantDetailModal = () => {
    const pb = selectedConsultant?.payoutBreakdown?.filterPeriod;
    const acc = selectedConsultant?._id ? accountingByConsultant[selectedConsultant._id] || {} : {};
    const last6MonthList = selectedConsultant?.payoutBreakdown?.last6Months || [];
    const dayPool = selectedConsultant?.payoutBreakdown?.last6MonthsDays || [];

    const stage1Rows = (last6MonthList || [])
      .filter((r) => r?.month)
      .map((r) => {
        const days = dayPool.filter((d) => d?.date && d.date.startsWith(r.month));
        const sumDays = (key) => days.reduce((total, day) => total + Number(day[key] || 0), 0);
        const grossRevenue = sumDays('totalRevenue');
        const walletAdd = sumDays('walletAdd');
        const walletDeduct = sumDays('grossDebits');
        const walletRefund = sumDays('walletRefund');
        const payableAfterShare = days.some((day) => day?.payableAfterShare != null)
          ? sumDays('payableAfterShare')
          : undefined;
        const ladder = deriveMonthPayoutLadder({
          totalRevenue: grossRevenue,
          walletAdd,
          walletDeduct,
          walletRefund,
          payableAfterShare,
        });
        const totalRevenue = Number(Number(ladder.netBeforeCharges).toFixed(2));
        return {
          ...r,
          chat: sumDays('chat'),
          call: sumDays('call'),
          fixedSession: sumDays('fixedSession'),
          gift: sumDays('gift'),
          totalRevenue,
          grossDebits: walletDeduct,
          walletRefund,
          walletAdd,
          monthLabel: moment(`${r.month}-01`).format('MMM YYYY'),
          totalOrders: sumDays('ordersCount'),
          monthPg: ladder.pg,
          monthTds: ladder.tds,
          monthFinal: roundPayoutRupee(ladder.finalPay),
        };
      });

    const trackingRowsAsc = [...(paymentTrackingRows.length ? paymentTrackingRows : last6MonthList)]
      .filter((r) => r?.month)
      .sort((a, b) => (a.month > b.month ? 1 : -1));
    let runningTdsAvailable = 0;
    const trackingRowsForDisplay = trackingRowsAsc
      .map((row) => {
        const summary = stage1Rows.find((r) => r.month === row.month) || {};
        const merged = mergePaymentTrackingForMonth(
          row.month,
          paymentTrackingByMonth,
          selectedConsultant._id,
          accountingByConsultant
        );
        const payableAmount = merged.payableAmount != null ? merged.payableAmount : summary.monthFinal ?? null;
        const holdAmount = merged.holdAmount != null ? merged.holdAmount : merged.dueAmount ?? null;
        const transferredAmount = merged.transferredAmount != null ? merged.transferredAmount : null;
        const tdsAmount = merged.tdsAmount != null ? merged.tdsAmount : summary.monthTds ?? null;
        runningTdsAvailable += Number(tdsAmount || 0);
        return {
          ...row,
          ...merged,
          monthLabel: row.month ? moment(`${row.month}-01`).format('MM-YYYY') : (summary.monthLabel || ''),
          grossAmount: merged.grossAmount != null ? merged.grossAmount : summary.totalRevenue ?? null,
          payableAmount,
          transferredAmount,
          holdAmount,
          tdsAmount,
          totalTdsAvailable: merged.totalTdsAvailable != null ? merged.totalTdsAvailable : runningTdsAvailable,
        };
      })
      .sort((a, b) => (a.month < b.month ? 1 : -1));

    const breakdownTableShell = {
      marginBottom: 12,
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(148, 163, 184, 0.1)',
      border: '1px solid #dce3ee',
      background: 'linear-gradient(180deg, #feffff 0%, #f6f9fc 100%)',
    };

    const paymentTrackingTableShell = {
      marginTop: 20,
      marginBottom: 8,
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(148, 163, 184, 0.06)',
      border: '1px solid #e8ecf0',
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    };

    const consultationHistoryColumns = [
      {
        title: 'Date / time',
        key: 'createdAt',
        width: 132,
        render: (_, r) => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
      },
      {
        title: 'Type',
        key: 'type',
        width: 100,
        ellipsis: true,
        render: (_, t) => <span style={{ textTransform: 'capitalize', color: '#a03d44', fontWeight: 600 }}>{getTxnTypeLabel(t)}</span>,
      },
      {
        title: 'Name',
        key: 'name',
        width: 120,
        ellipsis: true,
        render: (_, t) => t?.meta?.name || '—',
      },
      {
        title: 'Duration',
        key: 'dur',
        width: 82,
        render: (_, t) => formatTxnDuration(t),
      },
      {
        title: 'Detail',
        key: 'detail',
        width: 220,
        ellipsis: true,
        render: (_, t) => getTxnDetailLine(t),
      },
      {
        title: 'User ID',
        key: 'userId',
        width: 108,
        ellipsis: true,
        render: (_, t) => t?.meta?.userId || '—',
      },
      {
        title: 'Order ID',
        key: 'orderId',
        width: 108,
        ellipsis: true,
        render: (_, t) => t?.meta?.orderId || '—',
      },
      {
        title: 'Amount',
        key: 'coins',
        width: 100,
        align: 'right',
        render: (_, t) => {
          const ty = t?.transactionType === 0 ? 'credit' : 'debit';
          const amount = Number(t?.coins || 0);
          return (
            <span style={{ color: ty === 'credit' ? '#389e0d' : '#cf1322', fontWeight: 600 }}>
              {ty === 'debit' ? '-' : '+'}₹{amount.toFixed(2)}
            </span>
          );
        },
      },
      {
        title: 'Cr / Dr',
        key: 'crdr',
        width: 76,
        render: (_, t) => {
          const ty = t?.transactionType === 0 ? 'credit' : 'debit';
          return <Tag color={ty === 'credit' ? 'green' : 'red'}>{ty.toUpperCase()}</Tag>;
        },
      },
      {
        title: 'Mode',
        key: 'mode',
        width: 80,
        render: (_, t) =>
          t?.meta?.mode ? <Tag color="blue">{String(t.meta.mode).toUpperCase()}</Tag> : '—',
      },
    ];

    const consultationHistoryData = filterConsultationHistoryRows(
      selectedConsultant?.transactions || [],
      stage3TxnSearch
    );

    return (
      <Modal
        title={
          <div>
            <div>Payout — consultant detail</div>
            {selectedConsultant && (
              <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
                {selectedConsultant.accountName} • {selectedConsultant.filter} period
              </div>
            )}
          </div>
        }
        open={consultantDetailModalVisible}
        onCancel={() => setConsultantDetailModalVisible(false)}
        footer={null}
        width={1520}
        style={{ top: 20 }}
      >
        <Spin spinning={detailLoading} tip="Loading..." delay={150}>
          {selectedConsultant && (
            <div>
              <style>
                {`
              .vm-modal-earnings-table .ant-table-thead > tr > th {
                background: linear-gradient(180deg, #e8eef6 0%, #e0e8f2 100%) !important;
                color: #475569 !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                border-bottom: 1px solid #d3dce8 !important;
              }
              .vm-modal-earnings-table .ant-table-cell {
                border-inline: none !important;
              }
              .vm-modal-earnings-table .ant-table-tbody > tr > td {
                font-size: 14px !important;
                border-bottom: 1px solid #e8edf4 !important;
              }
              .vm-modal-earnings-table .ant-table-tbody > tr:nth-child(odd) > td {
                background: #fafcfe !important;
              }
              .vm-modal-earnings-table .ant-table-tbody > tr:nth-child(even) > td {
                background: #f4f7fb !important;
              }
              .vm-modal-earnings-table .ant-table-tbody > tr:hover > td {
                background: #e8f4fc !important;
              }
              .vm-payment-tracking-table .ant-table-thead > tr > th {
                background: linear-gradient(180deg, #f2f5f8 0%, #ebeef3 100%) !important;
                color: #475569 !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                border-bottom: 1px solid #e2e8ef !important;
              }
              .vm-payment-tracking-table .ant-table-cell {
                border-inline: none !important;
              }
              .vm-payment-tracking-table .ant-table-tbody > tr > td {
                font-size: 14px !important;
                border-bottom: 1px solid #eef1f5 !important;
              }
              .vm-payment-tracking-table .ant-table-tbody > tr:nth-child(odd) > td {
                background: #fcfcfd !important;
              }
              .vm-payment-tracking-table .ant-table-tbody > tr:nth-child(even) > td {
                background: #f6f8fa !important;
              }
              .vm-payment-tracking-table .ant-table-tbody > tr:hover > td {
                background: #f0f6fc !important;
              }
              .vm-consultation-history-table .ant-table-thead > tr > th {
                background: linear-gradient(180deg, #e8eef6 0%, #e0e8f2 100%) !important;
                color: #475569 !important;
                font-size: 15px !important;
                border-bottom: 1px solid #d3dce8 !important;
              }
              .vm-consultation-history-table .ant-table-cell {
                border-inline: none !important;
              }
              .vm-consultation-history-table .ant-table-tbody > tr > td {
                vertical-align: middle !important;
                font-size: 14px !important;
                border-bottom: 1px solid #e8edf4 !important;
              }
              .vm-consultation-history-table .ant-table-tbody > tr:nth-child(odd) > td {
                background: #fafcfe !important;
              }
              .vm-consultation-history-table .ant-table-tbody > tr:nth-child(even) > td {
                background: #f4f7fb !important;
              }
              .vm-consultation-history-table .ant-table-tbody > tr:hover > td {
                background: #e8f4fc !important;
              }
              .vm-stage1-month-row-active > td {
                background: #d6effc !important;
              }
              .vm-stage1-month-table.ant-table-wrapper .ant-table-container {
                border: 1px solid #d1dae8;
                border-radius: 8px;
                overflow: hidden;
              }
              .vm-stage1-month-table .ant-table-thead > tr > th,
              .vm-stage1-month-table .ant-table-tbody > tr > td {
                border-left: none !important;
                border-right: none !important;
                border-bottom: 1px solid #e8edf4 !important;
                text-align: center !important;
                vertical-align: middle !important;
              }
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg1 {
                background: linear-gradient(180deg, #eef2f7 0%, #e4eaf3 100%) !important;
              }
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg2 {
                background: linear-gradient(180deg, #f0f4f9 0%, #e6edf5 100%) !important;
              }
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg3 {
                background: linear-gradient(180deg, #f3f6fa 0%, #e9f0f7 100%) !important;
              }
              /* Light “box” outline per pair (Month+Orders | chat…refund | earning…final) */
              .vm-stage1-month-table .vm-s1-a1 { border-left: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .vm-s1-a2 { border-right: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .vm-s1-b1 { border-left: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .vm-s1-b2 { border-right: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .vm-s1-c1 { border-left: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .vm-s1-c2 { border-right: 1px solid #c5d2e5 !important; }
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg1,
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg2,
              .vm-stage1-month-table .ant-table-thead > tr > th.vm-s1-hg3 {
                border-top: 1px solid #c5d2e5 !important;
              }
              .vm-stage1-month-table .ant-table-tbody > tr:last-child > td:nth-child(1),
              .vm-stage1-month-table .ant-table-tbody > tr:last-child > td:nth-child(2) {
                border-bottom: 1px solid #c5d2e5 !important;
              }
              .vm-stage1-month-table .ant-table-tbody > tr:last-child > td:nth-child(n+3):nth-child(-n+7) {
                border-bottom: 1px solid #c5d2e5 !important;
              }
              .vm-stage1-month-table .ant-table-tbody > tr:last-child > td:nth-child(n+8):nth-child(-n+11) {
                border-bottom: 1px solid #c5d2e5 !important;
              }
            `}
              </style>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col flex="0px">
                  {/* <Card size="small" title="Accounts (this consultant)">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#888' }}>Payment date</div>
                      <DatePicker
                        style={{ width: '100%' }}
                        value={toPickerDayjs(acc.paymentDate)}
                        onChange={(d) =>
                          selectedConsultant._id &&
                          setAccountingField(selectedConsultant._id, {
                            paymentDate: d ? d.toISOString() : null,
                          })
                        }
                        format="DD MMM YYYY"
                      />
                    </div>
                    {/* <div>
                      <div style={{ fontSize: 12, color: '#888' }}>Payment status</div>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Select"
                        allowClear
                        value={acc.paymentStatus || undefined}
                        onChange={(v) =>
                          selectedConsultant._id &&
                          setAccountingField(selectedConsultant._id, { paymentStatus: v || null })
                        }
                      >
                        <Option value="Paid">Paid</Option>
                        <Option value="Pending">Pending</Option>
                        <Option value="Processing">Processing</Option>
                        <Option value="Unpaid">Unpaid</Option>
                        <Option value="Failed">Failed</Option>
                      </Select>
                    </div> */}
                  {/* <Link
                      to={`/consultant-management/consultant-detail/${selectedConsultant._id}?from=management`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open full profile
                    </Link> */}
                  {/* </Space>
                </Card> */}
                </Col>
                <Col flex="auto">
                  {pb && (
                    <Card
                      size="small"
                      title="Selected filter period — revenue split"
                      extra={
                        selectedConsultant.lastPayoutDate ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Last system payout: {moment(selectedConsultant.lastPayoutDate).format('DD MMM YYYY')}
                          </Text>
                        ) : null
                      }
                    >
                      <Row gutter={[12, 8]}>
                        <Col xs={12} sm={8}>
                          <Statistic
                            title="Wallet deduct (non-payout debits)"
                            value={pb.walletDeductNonPayoutDebits ?? 0}
                            precision={2}
                            prefix="₹"
                          />
                        </Col>
                        <Col xs={12} sm={8}>
                          <Statistic
                            title="Total earning (credits − deducts)"
                            value={selectedConsultant.availableBalanceForPeriod ?? 0}
                            precision={2}
                            prefix="₹"
                          />
                        </Col>
                        {/* <Col xs={12} sm={8}>
                        <Statistic title="Chat" value={selectedConsultant.periodChatEarning || 0} precision={2} prefix="₹" />
                      </Col>
                      <Col xs={12} sm={8}>
                        <Statistic title="Call" value={selectedConsultant.periodCallEarning || 0} precision={2} prefix="₹" />
                      </Col>
                      <Col xs={12} sm={8}>
                        <Statistic title="Fixed session" value={selectedConsultant.periodFixedSessionEarning || 0} precision={2} prefix="₹" />
                      </Col> */}
                        {/* <Col xs={12} sm={8}>
                        <Statistic
                          title="Earning w/o TDS, PG"
                          value={pb.consultantEarningWithoutTdsPg}
                          precision={2}
                          prefix="₹"
                        />
                      </Col> */}

                        <Col xs={12} sm={8}>
                          <Statistic
                            title="Payment gateway"
                            value={
                              ((Number(selectedConsultant.availableBalanceForPeriod) || 0) * PAYMENT_GATEWAY_PERCENT) / 100
                            }
                            precision={2}
                            prefix="₹"
                          />
                        </Col>
                        <Col xs={12} sm={8}>
                          <Statistic
                            title="TDS"
                            value={
                              (() => {
                                const net = Number(selectedConsultant.availableBalanceForPeriod) || 0;
                                const pg = (net * PAYMENT_GATEWAY_PERCENT) / 100;
                                return ((net - pg) * TDS_PERCENT) / 100;
                              })()
                            }
                            precision={2}
                            prefix="₹"
                          />
                        </Col>
                        <Col xs={12} sm={8}>
                          <Statistic
                            title="Final (after TDS, PG)"
                            value={
                              (() => {
                                const net = Number(selectedConsultant.availableBalanceForPeriod) || 0;
                                const pg = (net * PAYMENT_GATEWAY_PERCENT) / 100;
                                const afterPg = net - pg;
                                const tds = (afterPg * TDS_PERCENT) / 100;
                                return roundPayoutRupee(afterPg - tds);
                              })()
                            }
                            precision={0}
                            prefix="₹"
                          />
                        </Col>
                      </Row>
                    </Card>
                  )}
                </Col>
              </Row>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  alignItems: 'center',
                  marginBottom: 16,
                  padding: '12px 14px',
                  background: 'linear-gradient(180deg, #f6f8fb 0%, #f1f4f8 100%)',
                  borderRadius: 12,
                  border: '1px solid #e6eaf0',
                }}
              >
                {[
                  { key: '1', label: 'Stage 1', sub: 'Monthly payment tracking' },
                  { key: '2', label: 'Stage 2', sub: 'Day-wise' },
                  { key: '3', label: 'Stage 3', sub: 'Consultation history' },
                ].map((s) => {
                  const active = detailModalStage === s.key;
                  return (
                    <Button
                      key={s.key}
                      htmlType="button"
                      size="middle"
                      type="default"
                      onClick={() => setDetailModalStage(s.key)}
                      style={
                        active
                          ? {
                            background: 'linear-gradient(180deg, #e8f2ff 0%, #ddeaff 100%)',
                            borderColor: '#b6d4ff',
                            color: '#1e3a8a',
                            boxShadow: '0 1px 6px rgba(30, 64, 175, 0.14)',
                          }
                          : {
                            background: '#fff',
                            borderColor: '#c7d2e0',
                            color: '#475569',
                          }
                      }
                    >
                      <span style={{ fontWeight: 600 }}>{s.label}</span>
                      <span style={{ fontWeight: 400, opacity: 0.92, marginLeft: 6 }}>— {s.sub}</span>
                    </Button>
                  );
                })}
              </div>

              <Row gutter={[16, 16]} align="top" style={{ marginBottom: 8 }}>
                <Col xs={24}>
                  {detailModalStage === '1' && (
                    <div
                      style={{
                        maxHeight: 720,
                        overflowY: 'auto',
                        paddingRight: 6,
                        border: '1px solid #e8edf2',
                        borderRadius: 12,
                        padding: 16,
                        background: 'linear-gradient(165deg, #ffffff 0%, #fafbfc 50%, #f4f7fa 100%)',
                      }}
                    >
                      {/* <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <Text type="secondary" style={{ marginRight: 4 }}>
                        Day bucket reload:
                      </Text>
                      <Button
                        htmlType="button"
                        size="small"
                        type={breakdownRange === 'selected' ? 'primary' : 'default'}
                        onClick={async () => {
                          if (!selectedConsultant?._id) return;
                          setBreakdownRange('selected');
                          const payload = { filter: filters.filter };
                          if (filters.filter === 'custom' && filters.startDate && filters.endDate) {
                            payload.startDate = filters.startDate;
                            payload.endDate = filters.endDate;
                          }
                          await refetchConsultantWalletDetails(selectedConsultant._id, payload);
                        }}
                        style={breakdownRange === 'selected' ? {} : { background: '#fff', borderColor: '#cbd5e1' }}
                      >
                        Selected period
                      </Button>
                      <Button
                        htmlType="button"
                        size="small"
                        type={breakdownRange === 'currentmonth' ? 'primary' : 'default'}
                        onClick={async () => {
                          if (!selectedConsultant?._id) return;
                          setBreakdownRange('currentmonth');
                          await refetchConsultantWalletDetails(selectedConsultant._id, { filter: 'currentmonth' });
                        }}
                        style={breakdownRange === 'currentmonth' ? {} : { background: '#fff', borderColor: '#cbd5e1' }}
                      >
                        Current month
                      </Button>
                      <Button
                        htmlType="button"
                        size="small"
                        type={breakdownRange === 'lastmonth' ? 'primary' : 'default'}
                        onClick={async () => {
                          if (!selectedConsultant?._id) return;
                          setBreakdownRange('lastmonth');
                          await refetchConsultantWalletDetails(selectedConsultant._id, { filter: 'lastmonth' });
                        }}
                        style={breakdownRange === 'lastmonth' ? {} : { background: '#fff', borderColor: '#cbd5e1' }}
                      >
                        Last month
                      </Button>
                      <Button
                        htmlType="button"
                        size="small"
                        type={breakdownRange === 'last6months' ? 'primary' : 'default'}
                        onClick={async () => {
                          if (!selectedConsultant?._id) return;
                          setBreakdownRange('last6months');
                          await refetchConsultantWalletDetails(selectedConsultant._id, { filter: 'last6months' });
                        }}
                        style={breakdownRange === 'last6months' ? {} : { background: '#fff', borderColor: '#cbd5e1' }}
                      >
                        Last 6 months
                      </Button>
                    </div> */}

                      <div style={{ marginBottom: 8, fontWeight: 600, color: '#475569', fontSize: 16 }}>Month-wise summary</div>
                      {/*                     <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                      Months follow the wallet window from the server through the current month. <strong>Total orders</strong> sums daily order
                      counts. Revenue / deduct / refund / PG / TDS / final use the same rules as the consultant detail summary card (net = revenue −
                      wallet deduct + wallet refund, then PG {PAYMENT_GATEWAY_PERCENT}% and TDS {TDS_PERCENT}%). Use <strong>Stage 2</strong> for
                      day-wise drill-down.
                    </Text> */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => {
                            if (!stage1Rows.length) {
                              toast.error('No month data to download');
                              return;
                            }
                            const rows = stage1Rows.map((r) => ({
                              MonthKey: r.month,
                              Month: r.monthLabel,
                              TotalOrders: Number(r.totalOrders || 0),
                              Chat: Number(r.chat || 0).toFixed(2),
                              Call: Number(r.call || 0).toFixed(2),
                              FixedSession: Number(r.fixedSession || 0).toFixed(2),
                              GiftEarning: Number(r.gift || 0).toFixed(2),
                              TotalRevenueCredits: Number(r.totalRevenue || 0).toFixed(2),
                              WalletDeduct: Number(r.grossDebits || 0).toFixed(2),
                              WalletAddition: Number(r.walletAdd || 0).toFixed(2),
                              WalletRefund: Number(r.walletRefund || 0).toFixed(2),
                              PaymentGateway: Number(r.monthPg || 0).toFixed(2),
                              TDS: Number(r.monthTds || 0).toFixed(2),
                              FinalAfterTdsPg: String(roundPayoutRupee(r.monthFinal)),
                              TransferredAmount: '',
                              HoldAmount: '',
                              ShiftToNextMonth: '',
                              TdsDeducted: '',
                              TotalTdsAvailable: '',
                            }));
                            requestDownloadWithCallback(
                              () => downloadCsvFile(
                                `payout_monthwise_${(selectedConsultant.accountName || 'consultant').replace(/\s+/g, '_')}.csv`,
                                Object.keys(rows[0]),
                                rows,
                                `Stage 1 (Month-wise) for ${selectedConsultant.accountName || 'consultant'}`
                              ),
                              'Consultant Payout'
                            );
                          }}
                        >
                          Download Stage 1
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          style={{ marginLeft: 8 }}
                          onClick={() => {
                            if (!trackingRowsForDisplay.length) {
                              toast.error('No payment tracking data to download');
                              return;
                            }
                            const rows = buildConsultantPayoutCsvRows(selectedConsultant, trackingRowsForDisplay);
                            const headers = [
                              'Name',
                              'Email',
                              'DateOfJoining',
                              'Total Earning',
                              'PG (2.5%)',
                              'TDS (10%)',
                              'LastMonthFinalPayable',
                              'Balance',
                              'FinalPayableAmount',
                              'PAN',
                              'PhoneNumber',
                              'BankName',
                              'BankAccountNumber',
                              'BankIFSCCode',
                              'BankHolderName',
                              'PayoutMonth',
                              'TransactionId',
                              'PayoutDate',
                              'Status',
                              'Period',
                            ];
                            requestDownloadWithCallback(
                              () => downloadCsvFile(
                                `payout_tracking_${(selectedConsultant.accountName || 'consultant').replace(/\s+/g, '_')}.csv`,
                                headers,
                                rows,
                                `month-wise payout tracking for ${selectedConsultant.accountName || 'consultant'}`
                              ),
                              'Consultant Payout'
                            );
                          }}
                        >
                          Download tracking sheet
                        </Button>
                      </div>
                      <div style={breakdownTableShell}>
                        <Table
                          className="vm-modal-earnings-table vm-stage1-month-table"
                          size="small"
                          bordered={false}
                          pagination={false}
                          scroll={{ x: 1100 }}
                          tableLayout="fixed"
                          columns={breakdownStage1Cols}
                          dataSource={stage1Rows}
                          rowKey="month"
                          locale={{ emptyText: 'No month rows (use “Last 6 months” above if empty)' }}
                        />
                      </div>

                      <div
                        style={{
                          marginTop: 8,
                          padding: '14px 14px 16px',
                          borderRadius: 10,
                          border: '1px solid #e8ecf0',
                          background: 'linear-gradient(180deg, #fbfcfd 0%, #f5f7f9 100%)',
                        }}
                      >
                        <div style={{ marginBottom: 10, fontWeight: 600, color: '#64748b', fontSize: 14 }}>
                          Last 6 months — payment tracking
                        </div>
                        <div style={paymentTrackingTableShell}>
                          <div style={{ overflowX: 'auto' }} className="vm-stage-table-outer">
                            <Table
                              size="small"
                              pagination={false}
                              scroll={{ x: 1780 }}
                              rowKey="month"
                              className="vm-payment-tracking-table"
                              columns={[
                                { title: 'Pay month', dataIndex: 'monthLabel', key: 'monthLabel', width: 120 },
                                {
                                  title: 'Gross amount',
                                  dataIndex: 'grossAmount',
                                  key: 'grossAmount',
                                  width: 130,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Payable amount',
                                  dataIndex: 'payableAmount',
                                  key: 'payableAmount',
                                  width: 140,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Transferred',
                                  dataIndex: 'transferredAmount',
                                  key: 'transferredAmount',
                                  width: 120,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Hold',
                                  dataIndex: 'holdAmount',
                                  key: 'holdAmount',
                                  width: 110,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Next month',
                                  dataIndex: 'shiftToNextMonth',
                                  key: 'shiftToNextMonth',
                                  width: 110,
                                  render: (v) => (v ? 'Yes' : 'No'),
                                },
                                {
                                  title: 'Paid on',
                                  key: 'accMonthPaymentDate',
                                  width: 180,
                                  render: (_, row) => (row.paymentDate ? dayjs(row.paymentDate).format('DD MMM YYYY') : '—'),
                                },
                                {
                                  title: 'Payment status',
                                  key: 'accMonthPaymentStatus',
                                  width: 140,
                                  render: (_, row) => row.paymentStatus || '—',
                                },
                                {
                                  title: 'TDS deducted',
                                  dataIndex: 'tdsDeducted',
                                  key: 'tdsDeducted',
                                  width: 120,
                                  render: (v) => (v == null ? '—' : v ? 'Yes' : 'No'),
                                },
                                {
                                  title: 'TDS amount',
                                  dataIndex: 'tdsAmount',
                                  key: 'tdsAmount',
                                  width: 110,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Total TDS available',
                                  dataIndex: 'totalTdsAvailable',
                                  key: 'totalTdsAvailable',
                                  width: 150,
                                  align: 'right',
                                  render: (v) => (v != null ? rupee(v) : '—'),
                                },
                                {
                                  title: 'Due',
                                  key: 'accMonthDue',
                                  width: 110,
                                  align: 'right',
                                  render: (_, row) => (row.dueAmount != null ? rupee(row.dueAmount) : '—'),
                                },
                                {
                                  title: 'Reason',
                                  key: 'accMonthReason',
                                  width: 220,
                                  ellipsis: true,
                                  render: (_, row) => row.dueOrHoldReason || '—',
                                },
                              ]}
                              dataSource={trackingRowsForDisplay}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {detailModalStage === '2' && (
                    <div
                      style={{
                        maxHeight: 720,
                        overflowY: 'auto',
                        paddingRight: 6,
                        border: '1px solid #e8edf2',
                        borderRadius: 12,
                        padding: 16,
                        background: 'linear-gradient(165deg, #ffffff 0%, #fafbfc 50%, #f4f7fa 100%)',
                      }}
                    >
                      {/* <div style={{ marginBottom: 10, fontWeight: 600, color: '#475569', fontSize: 16 }}>Day-wise by month</div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                      Tap a month to show or hide its day-wise table. Active months are highlighted in blue; several active months stack in the
                      order you turned them on.
                    </Text> */}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 8,
                          alignItems: 'center',
                          marginBottom: 18,
                          padding: '10px 12px',
                          background: 'linear-gradient(180deg, #eef2f7 0%, #e8edf4 100%)',
                          borderRadius: 10,
                          border: '1px solid #dbe0ea',
                        }}
                      >
                        <Text type="secondary" style={{ marginRight: 2 }}>
                          Months:
                        </Text>
                        {stage1Rows.map((r) => {
                          const active = modalExpandedMonthKeys.includes(r.month);
                          return (
                            <Button
                              key={r.month}
                              htmlType="button"
                              size="small"
                              type={active ? 'primary' : 'default'}
                              onClick={() =>
                                setModalExpandedMonthKeys((prev) =>
                                  prev.includes(r.month) ? prev.filter((x) => x !== r.month) : [...prev, r.month]
                                )
                              }
                              style={
                                active
                                  ? { boxShadow: '0 1px 6px rgba(24, 144, 255, 0.22)' }
                                  : { background: '#fff', borderColor: '#c7d2e0', color: '#475569' }
                              }
                            >
                              {r.monthLabel}
                            </Button>
                          );
                        })}
                        {modalExpandedMonthKeys.length > 0 && (
                          <Button htmlType="button" size="small" type="link" onClick={() => setModalExpandedMonthKeys([])}>
                            Clear all
                          </Button>
                        )}
                      </div>
                      {modalExpandedMonthKeys.length === 0 && (
                        <div
                          style={{
                            padding: '20px 16px',
                            textAlign: 'center',
                            color: '#94a3b8',
                            background: '#f8fafc',
                            borderRadius: 10,
                            border: '1px dashed #cbd5e1',
                            marginBottom: 8,
                          }}
                        >
                          Select one or more months above to load their day-wise data.
                        </div>
                      )}
                      {modalExpandedMonthKeys.map((ymKey) => {
                        const monthDaysRaw = dayPool.filter((d) => d.date.startsWith(ymKey)).sort((a, b) => (a.date < b.date ? 1 : -1));
                        const monthDays = monthDaysRaw.map((row) => {
                          const ladder = deriveMonthPayoutLadder({
                            totalRevenue: Number(row.totalRevenue ?? 0),
                            walletAdd: Number(row.walletAdd ?? 0),
                            walletDeduct: Number(row.grossDebits ?? 0),
                            walletRefund: Number(row.walletRefund ?? 0),
                            payableAfterShare: row.payableAfterShare,
                          });
                          return {
                            ...row,
                            grossDebits: Number(row.grossDebits ?? 0),
                            walletRefund: Number(row.walletRefund ?? 0),
                            walletAdd: Number(row.walletAdd ?? 0),
                            monthPg: ladder.pg,
                            monthTds: ladder.tds,
                            monthFinal: roundPayoutRupee(ladder.finalPay),
                          };
                        });
                        const title = moment(`${ymKey}-01`).format('MMMM YYYY');
                        return (
                          <div key={ymKey} style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ fontWeight: 600, color: '#475569' }}>Day-wise — {title}</div>
                              <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => {
                                  if (!monthDays.length) {
                                    toast.error('No day rows');
                                    return;
                                  }
                                  const rows = monthDays.map((r) => ({
                                    Date: r.date,
                                    Orders: r.ordersCount,
                                    ActualShare: Number(r.actualShare) > 0 ? `${Number(r.actualShare)}%` : '',
                                    RevenueShare: Number(r.revenueShare) > 0 ? `${Number(r.revenueShare)}%` : '',
                                    EffectiveShare: Number(r.effectiveShare) > 0 ? `${Number(r.effectiveShare)}%` : '',
                                    Chat: Number(r.chat || 0).toFixed(2),
                                    Call: Number(r.call || 0).toFixed(2),
                                    Fixed: Number(r.fixedSession || 0).toFixed(2),
                                    WalletDeduct: Number(r.grossDebits || 0).toFixed(2),
                                    WalletAddition: Number(r.walletAdd || 0).toFixed(2),
                                    WalletRefund: Number(r.walletRefund || 0).toFixed(2),
                                    TotalEarning: Number(r.totalRevenue || 0).toFixed(2),
                                    PaymentGateway: Number(r.monthPg || 0).toFixed(2),
                                    TDS: Number(r.monthTds || 0).toFixed(2),
                                    FinalPayable: String(roundPayoutRupee(r.monthFinal)),
                                  }));
                                  requestDownloadWithCallback(
                                    () => downloadCsvFile(
                                      `payout_days_${ymKey}_${(selectedConsultant.accountName || 'consultant').replace(/\s+/g, '_')}.csv`,
                                      Object.keys(rows[0]),
                                      rows,
                                      `Stage 2 (Day-wise ${ymKey}) for ${selectedConsultant.accountName || 'consultant'}`
                                    ),
                                    'Consultant Payout'
                                  );
                                }}
                              >
                                Download Stage 2
                              </Button>
                            </div>
                            <div style={breakdownTableShell}>
                              <Table
                                className="vm-modal-earnings-table vm-stage1-month-table"
                                size="small"
                                bordered={false}
                                pagination={false}
                                scroll={{ x: 1320 }}
                                tableLayout="fixed"
                                columns={breakdownStage2DayCols}
                                dataSource={monthDays}
                                rowKey="date"
                                locale={{ emptyText: 'No transactions on these days' }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {detailModalStage === '3' && (
                    <div style={{ minHeight: 400, maxHeight: 720, overflow: 'auto' }}>
                      <div
                        style={{
                          border: '1px solid #e8edf2',
                          borderRadius: 12,
                          padding: 16,
                          background: 'linear-gradient(165deg, #ffffff 0%, #fafbfc 50%, #f4f7fa 100%)',
                        }}
                      >
                        <div style={{ marginBottom: 12, fontWeight: 600, color: '#475569', fontSize: 16 }}>Consultation history</div>
                        <div
                          style={{
                            marginBottom: 16,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '8px 12px',
                          }}
                        >
                          <span style={{ fontSize: 14, color: '#64748b', whiteSpace: 'nowrap' }}>
                            Period (same as main payout page)
                          </span>
                          <Select
                            style={{ width: 200, background: '#fff' }}
                            value={filters.filter}
                            onChange={(value) => patchDetailFiltersAndRefetch({ filter: value })}
                            placeholder="Select filter"
                          >
                            {filterOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                          {filters.filter === 'custom' && (
                            <RangePicker
                              value={rangePickerValue(filters.startDate, filters.endDate)}
                              onChange={(dates) => {
                                if (!dates) {
                                  patchDetailFiltersAndRefetch({
                                    startDate: undefined,
                                    endDate: undefined,
                                  });
                                  return;
                                }
                                patchDetailFiltersAndRefetch({
                                  startDate: dates[0].format('YYYY-MM-DD'),
                                  endDate: dates[1].format('YYYY-MM-DD'),
                                });
                              }}
                              style={{ width: 260 }}
                              getPopupContainer={popupContainer}
                            />
                          )}
                          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            Search transactions
                          </span>
                          <Input
                            allowClear
                            placeholder="Name, User ID, or Order ID"
                            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                            value={stage3TxnSearch}
                            onChange={(e) => setStage3TxnSearch(e.target.value)}
                            style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 420 }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 12,
                            flexWrap: 'wrap',
                            gap: 8,
                          }}
                        >
                          <h4 style={{ margin: 0, color: '#475569', fontSize: 16, fontWeight: 600 }}>Transactions ({filters.filter})</h4>
                          <Button icon={<DownloadOutlined />} onClick={() => requestDownloadWithCallback(downloadTransactionHistory, 'Consultant Payout')}>
                            Download Stage 3
                          </Button>
                        </div>
                        <Table
                          className="vm-consultation-history-table"
                          size="small"
                          pagination={false}
                          scroll={{ x: 1180, y: 420 }}
                          rowKey={(r, i) => r?._id || `${r?.transactionId || 't'}-${r?.createdAt || i}`}
                          columns={consultationHistoryColumns}
                          dataSource={consultationHistoryData}
                          locale={{
                            emptyText: (
                              <div style={{ textAlign: 'center', padding: '28px 16px', color: '#94a3b8' }}>
                                No transactions for this period
                              </div>
                            ),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Spin>
      </Modal>
    );
  };

  const payModalConsultant = paymentDetailConsultantId
    ? consultants.find((c) => String(c._id) === String(paymentDetailConsultantId)) ?? null
    : null;
  const payModalTrackedPayment = payModalConsultant
    ? mergeListRowPaymentDisplay(payModalConsultant._id, payModalConsultant)
    : {};
  const latestPayout = payModalConsultant?.latestPayout || {};
  const modalPayout = payModalConsultant
    ? {
        userId:
          latestPayout.userId ||
          latestPayout.consultantUserId ||
          payModalConsultant.userId ||
          latestPayout.consultantId ||
          payModalConsultant._id,
        name: latestPayout.name || payModalConsultant.accountName || payModalConsultant.name,
        amount:
          latestPayout.monthlyPayoutAmount != null
            ? latestPayout.monthlyPayoutAmount
            : roundPayoutRupee(payModalConsultant.payableBalance),
        bankName: latestPayout.bankName || payModalConsultant.bankName,
        accountNumber: latestPayout.accountNumber || payModalConsultant.accountNumber,
        ifsc: latestPayout.ifsc || payModalConsultant.ifsc,
        transactionId: latestPayout.transactionId,
        paymentDate:
          latestPayout.paymentDate ||
          payModalTrackedPayment.paymentDate,
        payDate:
          latestPayout.payOutDate ||
          payModalTrackedPayment.paymentDate ||
          payModalConsultant.lastPayoutDate,
        remarks: latestPayout.remarks,
      }
    : null;
  const paymentDetailItems = modalPayout
    ? [
        { label: 'User ID', value: modalPayout.userId || '---' },
        { label: 'User Name', value: modalPayout.name || '---' },
        { label: 'Bank Name', value: modalPayout.bankName || '---' },
        { label: 'Pay Month', value: modalPayout.paymentDate ? dayjs(modalPayout.paymentDate).format('MM-YYYY') : '---' },
        { label: 'AC_NO.', value: modalPayout.accountNumber || '---' },
        { label: 'IFSC Code', value: modalPayout.ifsc || '---' },
        { label: 'AMOUNT F', value: modalPayout.amount != null ? `${roundPayoutRupee(modalPayout.amount)} INR` : '---' },
        { label: 'Pay Date', value: modalPayout.payDate ? dayjs(modalPayout.payDate).format('DD MMM YYYY') : '---' },
        { label: 'Pay_UTR/Ref No.', value: modalPayout.transactionId || '---' },
        { label: 'Remarks', value: modalPayout.remarks || '---' },
      ]
    : [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Payout</h2>
        <Space wrap>
          <Select style={{ width: 200 }} value={filters.filter} onChange={(value) => handleFilterChange('filter', value)} placeholder="Select filter">
            {filterOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>

          {filters.filter === 'custom' && (
            <RangePicker
              value={rangePickerValue(filters.startDate, filters.endDate)}
              onChange={(dates) => {
                setFilters((prev) => ({
                  ...prev,
                  startDate: dates ? dates[0].format('YYYY-MM-DD') : undefined,
                  endDate: dates ? dates[1].format('YYYY-MM-DD') : undefined,
                }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
              style={{ width: 250 }}
              getPopupContainer={popupContainer}
            />
          )}

          <Button icon={<DownloadOutlined />} onClick={handleDownloadExcel}>
            Download Excel
          </Button>

          <Button icon={<DownloadOutlined />} onClick={handleDownloadBankPanPendingExcel}>
            Bank/PAN Not Approved
          </Button>

          <Button icon={<DownloadOutlined />} onClick={handleUserDownloadExcel}>
            User Download Excel
          </Button>

          <Button icon={<DownloadOutlined />} onClick={handleConsultantDownloadExcel}>
            Consultant Monthly Data
          </Button>

          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModalVisible(true)}>
            Upload / Edit Settlement
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by consultant ID, name, email, or phone"
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          style={{ width: 400, maxWidth: '100%' }}
        />
      </div>

      <div style={{ overflowX: 'auto', width: '100%' }}>
        <div
          className="vm-main-payout-table-wrap"
          style={{
            border: `1px solid ${WALLET_HEADER_RULE}`,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <style>
            {`
            .vm-main-payout-table-wrap .ant-table .ant-table-container { border: none !important; }
            .vm-main-payout-table-wrap .ant-table-tbody > tr > td { border-bottom: none !important; }
            .vm-main-payout-table-wrap .ant-table-thead > tr > th {
              border-inline: none !important;
              border-top: none !important;
            }
            .vm-main-payout-table-wrap .ant-table-thead > tr > th.ant-table-cell { border-bottom: 1px solid ${WALLET_HEADER_RULE} !important; }
            .vm-main-payout-table-wrap .ant-table { font-size: ${WALLET_TABLE_FONT}px; }
            .vm-main-payout-table-wrap .ant-table-pagination { margin: 12px 16px 16px; }
          `}
          </style>
          <Table
            className="vm-main-payout-table"
            columns={columns}
            dataSource={consultants}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="_id"
            scroll={{ x: 2780 }}
            size="small"
          />
        </div>
      </div>

      <Modal
        title={payModalConsultant ? `Payment details — ${payModalConsultant.accountName}` : 'Payment details'}
        className="vm-payment-detail-modal"
        open={paymentDetailModalOpen}
        destroyOnClose
        onCancel={closePaymentDetailModal}
        footer={
          <div className="vm-payment-detail-footer">
            <Button type="primary" onClick={closePaymentDetailModal} className="vm-payment-save-btn">
              Close
            </Button>
          </div>
        }
        width={1080}
        getContainer={() => document.body}
      >
        {payModalConsultant ? (
          <>
            <style>
              {`
                .vm-payment-detail-modal .ant-modal-content {
                  border-radius: 12px;
                  overflow: hidden;
                  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
                  padding: 0;
                }
                .vm-payment-detail-modal .ant-modal-header {
                  margin: 0;
                  padding: 18px 24px 14px;
                  border-bottom: 1px solid #eef2f7;
                }
                .vm-payment-detail-modal .ant-modal-close {
                  top: 14px;
                  inset-inline-end: 16px;
                  border-radius: 8px;
                }
                .vm-payment-detail-modal .ant-modal-body {
                  padding: 20px 24px;
                  background: #fbfcfe;
                  max-height: calc(100vh - 180px);
                  overflow-y: auto;
                }
                .vm-payment-detail-modal .ant-modal-footer {
                  margin: 0;
                  padding: 14px 24px;
                  border-top: 1px solid #eef2f7;
                  background: #ffffff;
                }
                .vm-payment-detail-modal .ant-modal-title {
                  color: #111827;
                  font-size: 16px;
                  font-weight: 700;
                }
                .vm-payment-payout-grid {
                  display: grid;
                  grid-template-columns: repeat(5, minmax(0, 1fr));
                  border: 1px solid #e2e8f0;
                  border-radius: 10px;
                  overflow: hidden;
                  background: #fff;
                }
                .vm-payment-payout-item {
                  min-width: 0;
                  padding: 14px 13px;
                  border-right: 1px solid #eef2f7;
                  border-bottom: 1px solid #eef2f7;
                }
                .vm-payment-payout-item:nth-child(5n) {
                  border-right: none;
                }
                .vm-payment-payout-item:nth-last-child(-n+5) {
                  border-bottom: none;
                }
                .vm-payment-payout-label {
                  margin-bottom: 8px;
                  color: #64748b;
                  font-size: 11px;
                  font-weight: 700;
                  text-transform: uppercase;
                  letter-spacing: 0.03em;
                }
                .vm-payment-payout-value {
                  color: #111827;
                  font-size: 13px;
                  line-height: 1.4;
                  font-weight: 600;
                  overflow-wrap: anywhere;
                }
                .vm-payment-payout-item:nth-child(7) .vm-payment-payout-value {
                  color: #059669;
                  font-weight: 700;
                }
                .vm-payment-payout-item:nth-child(8) .vm-payment-payout-value {
                  color: #2563eb;
                  font-size: 12px;
                }
                .vm-payment-section {
                  padding: 16px 16px 2px;
                  border: 1px solid #e5e7eb;
                  border-radius: 10px;
                  background: #ffffff;
                }
                .vm-payment-section + .vm-payment-section {
                  margin-top: 14px;
                }
                .vm-payment-section-title {
                  margin-bottom: 14px;
                  color: #334155;
                  font-size: 13px;
                  font-weight: 700;
                }
                .vm-payment-detail-modal .ant-form-item {
                  margin-bottom: 16px;
                }
                .vm-payment-detail-modal .ant-form-item-label {
                  padding-bottom: 5px;
                }
                .vm-payment-detail-modal .ant-form-item-label > label {
                  color: #334155;
                  font-size: 12px;
                  font-weight: 700;
                }
                .vm-payment-detail-modal .ant-input,
                .vm-payment-detail-modal .ant-input-number,
                .vm-payment-detail-modal .ant-picker,
                .vm-payment-detail-modal .ant-select-selector {
                  min-height: 38px;
                  border-radius: 7px !important;
                  border-color: #d9e1ea !important;
                  box-shadow: none !important;
                }
                .vm-payment-detail-modal textarea.ant-input {
                  min-height: 92px;
                  resize: vertical;
                }
                .vm-payment-switch-row .ant-form-item-control-input {
                  min-height: 38px;
                }
                .vm-payment-detail-footer {
                  display: flex;
                  justify-content: flex-end;
                  gap: 10px;
                }
                .vm-payment-save-btn {
                  min-width: 92px;
                  border-radius: 7px;
                  font-weight: 600;
                }
                @media (max-width: 640px) {
                  .vm-payment-detail-modal {
                    max-width: calc(100vw - 24px);
                    margin: 12px auto;
                  }
                  .vm-payment-detail-modal .ant-modal-header,
                  .vm-payment-detail-modal .ant-modal-body,
                  .vm-payment-detail-modal .ant-modal-footer {
                    padding-left: 16px;
                    padding-right: 16px;
                  }
                  .vm-payment-payout-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                  }
                  .vm-payment-payout-item:nth-child(5n) {
                    border-right: 1px solid #eef2f7;
                  }
                  .vm-payment-payout-item:nth-child(2n) {
                    border-right: none;
                  }
                  .vm-payment-payout-item:nth-last-child(-n+5) {
                    border-bottom: 1px solid #eef2f7;
                  }
                  .vm-payment-payout-item:nth-last-child(-n+2) {
                    border-bottom: none;
                  }
                }
              `}
            </style>
            <div className="vm-payment-payout-grid">
              {paymentDetailItems.map((item) => (
                <div className="vm-payment-payout-item" key={item.label}>
                  <div className="vm-payment-payout-label">{item.label}</div>
                  <div className="vm-payment-payout-value">{item.value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#334155' }}>Month-wise remaining / overpaid</div>
              <Table
                size="small"
                loading={remainingHistoryLoading}
                pagination={false}
                rowKey={(row) => row.period || row.payOutDate || Math.random()}
                dataSource={remainingHistory}
                locale={{ emptyText: 'No settlement remaining history yet.' }}
                columns={[
                  { title: 'Month', dataIndex: 'period', key: 'period' },
                  { title: 'This month payable', key: 'thisMonthPayable', render: (_, r) => rupeeRounded(r.thisMonthPayable) },
                  {
                    title: 'Prev +/−',
                    key: 'addOn',
                    render: (_, r) => (
                      <span style={{ color: adjustmentColor(r.addOnAmount), fontWeight: 600 }}>
                        {signedRupee(r.addOnAmount)}
                        {Number(r.addOnAmount || 0) !== 0 && r.addOnFrom ? (
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                            {Number(r.addOnAmount) > 0 ? 'add' : 'deduct'} {r.addOnFrom}
                          </div>
                        ) : null}
                      </span>
                    ),
                  },
                  { title: 'Paid', key: 'paid', render: (_, r) => rupeeRounded(r.paid) },
                  {
                    title: 'This month +/−',
                    key: 'thisMonthPending',
                    render: (_, r) => (
                      <span style={{ color: adjustmentColor(r.thisMonthPending), fontWeight: 600 }}>
                        {signedRupee(r.thisMonthPending)}
                      </span>
                    ),
                  },
                  {
                    title: 'Goes to next payout',
                    key: 'next',
                    render: (_, r) => r.pendingGoesToPeriod || 'Next month',
                  },
                ]}
              />
            </div>
          </>
        ) : (
          <Text type="secondary">Consultant not found in the current list.</Text>
        )}
      </Modal>

      <Modal title="Upload payout settlement" open={uploadModalVisible} onCancel={() => setUploadModalVisible(false)} footer={null} width={640}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              padding: 16,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
            }}
          >
            <div style={{ fontWeight: 600, color: '#52c41a', marginBottom: 8 }}>Sheet upload</div>
            <div style={{ fontSize: 14, color: '#666' }}>&bull; Use MM-YYYY for PAYMENT DATE and DD/MM/YYYY for PAY DATE.</div>
            <div style={{ fontSize: 14, color: '#666' }}>&bull; To edit a month, re-upload a wrong file, or see logs, open Payout Management and select that month.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadSettlementTemplate} loading={exportbuttonLoader}>
                Download blank template
              </Button>
            </div>
          </div>
          <div>
            <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Upload settlement file</label>
            <Upload
              disabled={uploadBusy}
              beforeUpload={(file) => {
                const isExcel =
                  file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                  file.type === 'application/vnd.ms-excel';
                if (!isExcel) {
                  message.error('You can only upload Excel files.');
                  return false;
                }
                handleUploadExcel(file);
                return false;
              }}
              accept=".xlsx,.xls"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} type="primary" size="large" loading={uploadBusy} style={{ width: '100%', height: 50 }}>
                {uploadBusy ? 'Updating payout…' : 'Upload settlement file'}
              </Button>
            </Upload>
          </div>
        </div>
      </Modal>

      {renderConsultantDetailModal()}
      <DownloadPasswordModal />
    </div>
  );
};

export default ConsultantWallet;
